/*!
 * SeattleWallWraps.com · Builder C · Wall calculators ("Spatial Scan" AR measure UI)
 * Vanilla JS, no dependencies. window.CWV.calc
 *
 * Calculators (section setting `calculator`):
 *   wall_cost               Wall wrap size estimator. Modes: full (default) | teaser | roi_lens.
 *                           Always shows sq ft, printed panels and printed material. Dollar figures
 *                           only appear when config_json has a positive `rate_per_sqft`.
 *   wall_vs_paint_wallpaper Paint vs wallpaper vs wall wrap: cost over time (visitor prices).
 *   reface_vs_replace       Architectural film refacing vs replacing (keep unpublished until confirmed).
 * Specs: content/pages/wall-wrap-cost.json + roi-of-branded-spaces.json (calculator sections' `spec`)
 * and research/history-roi-facts.json calculators[]. See theme-spec/NOTES-C.md for config_json keys.
 *
 * Mount: <div data-cwv-calc="wall_cost" data-mode="full"> inside [data-cwv-calc-section]; optional
 * <script type="application/json" data-cwv-calc-config>{...}</script> in the same section.
 */
(function (root) {
  'use strict';

  var CWV = root.CWV = root.CWV || {};
  if (CWV.calc && CWV.calc.__loaded) { return; }

  /* ================================================================ helpers */
  function isNum(x) { return typeof x === 'number' && isFinite(x); }
  function num(x) {
    if (isNum(x)) { return x; }
    if (typeof x === 'string' && x.trim() !== '' && isFinite(+x)) { return +x; }
    return null;
  }
  /* Math.ceil with a guard so float noise (2.0000000001) can't add an install or a panel */
  function ceilG(x) { return Math.ceil(x - 1e-9); }
  function roundTo(x, r) { return (isNum(r) && r > 0) ? Math.round(x / r) * r : x; }
  function clamp(x, lo, hi) {
    if (isNum(lo) && x < lo) { x = lo; }
    if (isNum(hi) && x > hi) { x = hi; }
    return x;
  }
  function clone(o) { return o == null ? o : JSON.parse(JSON.stringify(o)); }
  function own(o, k) { return o != null && Object.prototype.hasOwnProperty.call(o, k); }
  function each(o, fn) { Object.keys(o || {}).forEach(function (k) { fn(o[k], k); }); }

  /* ================================================================ definitions */
  /* Text below is taken from the content specs. tag = how an assumption is labeled on screen. */
  var TAGS = {
    sourced: 'Sourced', inferred: 'Inferred', placeholder: 'Placeholder', assumption: 'Assumption',
    example: 'Example', visitor: 'Your number', missing: 'Set by Ardor', display: 'Display', choice: 'Your choice'
  };

  var WALL_FIELDS = [
    { id: 'width_ft', label: 'Wall width', short: 'Width', type: 'number', unit: 'ft', 'default': 12, min: 1, max: 200, step: 0.5, help: 'Measure along the floor, corner to corner.', source: 'Assumption: example size (matches the research worked example)', tag: 'example' },
    { id: 'height_ft', label: 'Wall height', short: 'Height', type: 'number', unit: 'ft', 'default': 9, min: 1, max: 40, step: 0.5, help: 'Floor to ceiling, or to the top of the area you want covered.', source: 'Assumption: example size', tag: 'example' }
  ];

  var WALL_INPUTS = {
    walls: { id: 'walls', label: 'Walls', type: 'repeater', min_items: 1, max_items: 10, add_label: 'Add another wall', help: 'Add each wall you want covered.', fields: WALL_FIELDS, 'default': [{ width_ft: 12, height_ft: 9 }] },
    openings_sqft: { id: 'openings_sqft', label: 'Doors and windows to subtract', type: 'number', unit: 'sq ft', 'default': 0, min: 0, max: 10000, step: 1, help: 'Rule of thumb: a standard 3 by 7 ft door is about 21 sq ft.', source: 'Assumption (arithmetic)', tag: 'assumption' },
    finish: {
      id: 'finish', label: 'Film and finish', type: 'select', 'default': 'smooth_removable',
      options: [
        { value: 'smooth_removable', label: 'Smooth wall, removable film' },
        { value: 'smooth_permanent', label: 'Smooth wall, permanent film' },
        { value: 'textured_film', label: 'Textured-look film (canvas, crushed stone or stucco)' },
        { value: 'laminated', label: 'Laminated finish (matte or gloss)' },
        { value: 'exterior', label: 'Exterior-rated film' },
        { value: 'floor', label: 'Floor graphic with slip-resistant laminate' }
      ],
      help: 'Not sure? Pick removable. We confirm the film at the on-site measure.', source: 'Film types listed on Ardor\'s wall wraps page'
    },
    surface: {
      id: 'surface', label: 'Wall surface', type: 'select', 'default': 'smooth_drywall',
      options: [
        { value: 'smooth_drywall', label: 'Smooth painted drywall' },
        { value: 'light_texture', label: 'Lightly textured paint' },
        { value: 'heavy_texture', label: 'Heavy texture, block or brick' },
        { value: 'glass', label: 'Glass' },
        { value: 'fresh_paint', label: 'Freshly painted (still curing)' }
      ],
      help: 'Smooth, cured semi-gloss paint is the easiest surface for wall film.', source: 'Avery IB 1.6; 3M substrate bulletin'
    },
    access: {
      id: 'access', label: 'Access', type: 'select', 'default': 'ground',
      options: [
        { value: 'ground', label: 'Ground level, standard ceiling' },
        { value: 'ladder_high', label: 'Ladder work or high ceiling' },
        { value: 'lift', label: 'Lift needed (exterior or very tall wall)' },
        { value: 'after_hours', label: 'After-hours install' }
      ],
      help: 'Lifts, tall walls and after-hours work add install time.', source: 'Ardor wall wraps page and wall guide'
    },
    artwork: {
      id: 'artwork', label: 'Artwork', type: 'select', 'default': 'print_ready',
      options: [
        { value: 'print_ready', label: 'I have print-ready art' },
        { value: 'ardor_from_logo', label: 'Ardor designs from my logo and photos' },
        { value: 'custom_mural', label: 'Custom mural designed from scratch' }
      ],
      help: 'Design is quoted separately from the wall wrap.', source: 'Ardor: design is quoted separately'
    }
  };

  var LIFE_OPTIONS = [
    { value: 2, label: '2 years: 3M\'s expected indoor life for its IJ8624 textured-wall film (not a warranty)', source: '3M IJ8624 product bulletin, Rev. E, Sept 2017' },
    { value: 3, label: '3 years: Avery\'s printed indoor wall rating for MPI 2610/2611 smooth-wall film (not a warranty)', source: 'Avery Dennison MPI 2600 Series data sheet, Rev. 17' },
    { value: 5, label: '5 years: Ardor\'s wall guide says indoor wall wraps often last five years or more', source: 'ardorprinting.com/blogs/news/seattle-wall-wraps-and-coverings' }
  ];

  var LENS_INPUTS = {
    project_cost: { id: 'project_cost', label: 'Project cost', type: 'number', unit: '$', money: true, 'default': null, placeholder: '[your quote]', min: 0, max: 1000000, step: 10, help: 'Enter your quote or a number you want to test.', source: 'Visitor-entered', tag: 'visitor' },
    years_on_wall: { id: 'years_on_wall', label: 'Years the graphic stays up', type: 'select_or_number', unit: 'years', 'default': 3, options: LIFE_OPTIONS, min: 0.5, max: 15, step: 0.5, help: 'Pick a scenario or type your own. Manufacturer ratings are not warranties.', source: 'See each option' },
    days_open_per_year: { id: 'days_open_per_year', label: 'Days the space is open per year', type: 'number', unit: 'days', 'default': 250, min: 1, max: 365, step: 1, help: 'Assumption: about 5 days a week for 50 weeks. Change it to fit your space.', source: 'Assumption', tag: 'assumption' },
    people_per_day: { id: 'people_per_day', label: 'People who see the wall on a typical day', type: 'number', unit: 'people', 'default': null, placeholder: '[enter]', min: 1, max: 100000, step: 1, help: 'Staff plus visitors who pass or use the space.', source: 'Visitor-entered', tag: 'visitor' },
    employees: { id: 'employees', label: 'Employees who work in the space (optional)', type: 'number', unit: 'people', 'default': null, placeholder: '[optional]', min: 1, max: 10000, step: 1, source: 'Visitor-entered', tag: 'visitor' }
  };

  var WALL_SETTINGS = [
    { id: 'rollWidthIn', label: 'Wall film roll width', unit: 'in', 'default': 54, min: 12, max: 130, step: 1, tag: 'sourced', source: 'Ardor Pricing, Policies & Production Specs (54-inch wall film)' },
    { id: 'usablePrintWidthIn', label: 'Usable print width per panel', unit: 'in', 'default': 52, min: 6, max: 130, step: 0.5, tag: 'inferred', source: 'Inferred placeholder: 1 in margin per side, as with Ardor\'s 60-in rolls (58 in printable). Ardor Pricing doc (60-in roll rule)' },
    { id: 'panelOverlapIn', label: 'Panel overlap at each seam', unit: 'in', 'default': 0.5, min: 0, max: 6, step: 0.25, tag: 'sourced', source: 'Avery IB 1.6: overlap multi-panel graphics at least 0.5 in (13 mm). Sourced minimum; installer to confirm Ardor\'s standard.' },
    { id: 'trimAllowanceFt', label: 'Extra panel height for trimming (total)', unit: 'ft', 'default': 0.5, min: 0, max: 3, step: 0.25, tag: 'placeholder', source: 'Placeholder' },
    { id: 'rangePct', label: 'Estimate range', unit: '+/- %', 'default': 15, min: 0, max: 60, step: 1, tag: 'placeholder', source: 'Placeholder', price: true },
    { id: 'roundTo', label: 'Round dollar outputs to', unit: '$', 'default': 10, min: 1, max: 1000, step: 1, tag: 'display', source: 'Display choice', price: true },
    { id: 'finishMultipliers', label: 'Finish multipliers', type: 'map', keys: 'finish', unit: 'x', step: 0.05, min: 0, max: 10, tag: 'placeholder', source: 'Placeholder values from the research layout. No source. Ardor sets these before price mode.', price: true,
      'default': { smooth_removable: 1.0, smooth_permanent: 1.0, textured_film: 1.25, laminated: 1.1, exterior: 1.3, floor: 1.4 } },
    { id: 'surfaceMultipliers', label: 'Surface multipliers', type: 'map', keys: 'surface', unit: 'x', step: 0.05, min: 0, max: 10, tag: 'placeholder', source: 'Placeholder. Ardor sets these.', price: true,
      'default': { smooth_drywall: 1.0, light_texture: 1.1, heavy_texture: 1.3, glass: 1.0, fresh_paint: 1.0 } },
    { id: 'accessMultipliers', label: 'Access multipliers', type: 'map', keys: 'access', unit: 'x', step: 0.05, min: 0, max: 10, tag: 'placeholder', source: 'Placeholder. Ardor sets these.', price: true,
      'default': { ground: 1.0, ladder_high: 1.1, lift: 1.25, after_hours: 1.15 } },
    { id: 'designFee', label: 'Design fee by artwork choice', type: 'map', keys: 'artwork', unit: '$', money: true, nullable: true, step: 10, min: 0, max: 100000, tag: 'placeholder', source: 'Design is quoted separately; no wall design price is published. Blank means "quoted separately".', price: true,
      'default': { print_ready: 0, ardor_from_logo: null, custom_mural: null } }
  ];

  var VS_INPUTS = {
    area_sqft: { id: 'area_sqft', label: 'Wall area', type: 'number', unit: 'sq ft', 'default': 400, min: 10, max: 20000, step: 10, help: 'Use the area from the size estimator or your own measurement.', source: 'Assumption: example value from the research worked example', tag: 'example' },
    horizon_years: { id: 'horizon_years', label: 'Time horizon', type: 'number', unit: 'years', 'default': 10, min: 1, max: 30, step: 1, help: 'How long you expect to keep this space.', source: 'Assumption', tag: 'assumption' },
    include_final_removal: { id: 'include_final_removal', label: 'Include removal at the end', type: 'toggle', 'default': false, help: 'Turn on if you will move out or change the wall at the end.', source: 'Visitor choice', tag: 'choice' },
    downtime_cost_per_day: { id: 'downtime_cost_per_day', label: 'Cost of a day the space is disrupted', type: 'number', unit: '$', money: true, 'default': 0, min: 0, max: 100000, step: 50, help: 'Lost sales, moved meetings or overtime. Leave at 0 to ignore.', source: 'Visitor-entered', tag: 'visitor' }
  };

  function vsA(id, label, unit, def, min, max, step, tag, source, extra) {
    var a = { id: id, label: label, unit: unit, 'default': def, min: min, max: max, step: step, tag: tag, source: source, type: 'number' };
    if (unit === '$/sq ft') { a.money = true; }
    each(extra || {}, function (v, k) { a[k] = v; });
    return a;
  }
  var VS_OPTIONS = [
    { id: 'paint', label: 'Paint (solid color)', short: 'Paint', visible: true, note: 'Cost-only comparison. A painted solid color does not carry a printed image.', assumptions: [
      vsA('install_cost_per_sqft', 'Installed cost', '$/sq ft', null, 0, 100, 0.05, 'visitor', 'No source fetched; enter your painter\'s price', { placeholder: '[your painter\'s price]' }),
      vsA('life_years', 'Years before repainting', 'years', null, 0.5, 30, 0.5, 'visitor', 'Not sourced', { placeholder: '[enter]' }),
      vsA('removal_cost_per_sqft', 'Removal or prep cost per cycle', '$/sq ft', 0, 0, 50, 0.05, 'assumption', 'Assumption: repainting goes over the old coat'),
      vsA('maintenance_per_sqft_per_year', 'Upkeep per year', '$/sq ft', 0, 0, 20, 0.05, 'assumption', 'Assumption'),
      vsA('downtime_days', 'Days disrupted per cycle', 'days', null, 0, 60, 0.5, 'visitor', 'Not sourced', { placeholder: '[enter]' })
    ] },
    { id: 'wallpaper', label: 'Wallpaper', short: 'Wallpaper', visible: true, assumptions: [
      vsA('install_cost_per_sqft', 'Installed cost (paper plus labor)', '$/sq ft', null, 0, 150, 0.05, 'visitor', 'Enter your installer\'s price', { placeholder: '[your installer\'s price]' }),
      vsA('life_years', 'Years before replacing', 'years', null, 0.5, 30, 0.5, 'visitor', 'Not sourced', { placeholder: '[enter]' }),
      vsA('removal_cost_per_sqft', 'Removal cost per cycle', '$/sq ft', null, 0, 50, 0.05, 'visitor', 'Not sourced', { placeholder: '[enter]' }),
      vsA('maintenance_per_sqft_per_year', 'Upkeep per year', '$/sq ft', 0, 0, 20, 0.05, 'assumption', 'Assumption'),
      vsA('downtime_days', 'Days disrupted per cycle', 'days', null, 0, 60, 0.5, 'visitor', 'Not sourced', { placeholder: '[enter]' })
    ] },
    { id: 'wall_wrap', label: 'Wall wrap (printed film)', short: 'Wall wrap', visible: true, accent: true, assumptions: [
      vsA('install_cost_per_sqft', 'Installed cost', '$/sq ft', null, 0, 150, 0.05, 'missing', 'Ardor quotes every wall after a free on-site measure. Enter a quote you have, or leave blank.', { placeholder: '[Ardor rate per sq ft]' }),
      vsA('life_years', 'Years on the wall', 'years', 3, 0.5, 15, 0.5, 'sourced', 'Default 3 = Avery smooth-wall film rating. Pick a scenario or type your own.', { type: 'select_or_number', options: LIFE_OPTIONS }),
      vsA('removal_cost_per_sqft', 'Removal cost per cycle', '$/sq ft', null, 0, 50, 0.05, 'missing', 'Not published by Ardor. Enter a quote you have, or leave blank.', { placeholder: '[Ardor removal rate]' }),
      vsA('maintenance_per_sqft_per_year', 'Upkeep per year', '$/sq ft', 0, 0, 20, 0.05, 'assumption', 'Assumption: wipe-clean laminate'),
      vsA('downtime_days', 'Days disrupted per cycle', 'days', 1, 0, 60, 0.5, 'sourced', 'Ardor wall wraps page: a single wall usually installs in one day; larger projects take several days; after-hours installs available')
    ] },
    { id: 'painted_mural', label: 'Hand-painted mural (optional)', short: 'Painted mural', visible: false, assumptions: [
      vsA('install_cost_per_sqft', 'Artist\'s price', '$/sq ft', null, 0, 500, 0.05, 'visitor', 'No source fetched; enter the artist\'s quote', { placeholder: '[artist\'s quote]' }),
      vsA('life_years', 'Years before repainting', 'years', null, 0.5, 50, 0.5, 'visitor', 'Not sourced', { placeholder: '[enter]' }),
      vsA('removal_cost_per_sqft', 'Removal or paint-over cost', '$/sq ft', null, 0, 50, 0.05, 'visitor', 'Not sourced', { placeholder: '[enter]' }),
      vsA('maintenance_per_sqft_per_year', 'Upkeep per year', '$/sq ft', 0, 0, 20, 0.05, 'assumption', 'Assumption'),
      vsA('downtime_days', 'Days disrupted per cycle', 'days', null, 0, 120, 0.5, 'visitor', 'Not sourced', { placeholder: '[enter]' })
    ] }
  ];

  var RF_INPUTS = {
    item_type: {
      id: 'item_type', label: 'What are you refacing?', type: 'select', 'default': 'door',
      options: [
        { value: 'door', label: 'Door' }, { value: 'desk', label: 'Desk or work surface' },
        { value: 'cabinet', label: 'Cabinet or casework front' }, { value: 'column', label: 'Column' },
        { value: 'reception_counter', label: 'Reception counter' }, { value: 'elevator', label: 'Elevator interior' },
        { value: 'other', label: 'Other' }
      ],
      help: 'Horizontal, high-wear surfaces like counters need an abrasion-resistant film. 3M says standard film loses durability when it isn\'t vertical.',
      source: '3M DI-NOC data sheets (Rev. S and Rev. T); AR Series data sheet'
    },
    quantity: { id: 'quantity', label: 'Number of items', type: 'number', unit: 'items', 'default': 10, min: 1, max: 1000, step: 1, source: 'Assumption: example', tag: 'example' },
    area_per_item_sqft: { id: 'area_per_item_sqft', label: 'Surface area per item', type: 'number', unit: 'sq ft', 'default': 21, min: 1, max: 500, step: 0.5, help: 'Rule of thumb: one face of a 3 by 7 ft door is about 21 sq ft.', source: 'Assumption (arithmetic)', tag: 'assumption' },
    replace_cost_per_item: { id: 'replace_cost_per_item', label: 'Price of a new unit', type: 'number', unit: '$', money: true, 'default': null, placeholder: '[price of a new unit]', min: 0, max: 500000, step: 10, source: 'Visitor-entered', tag: 'visitor' },
    replace_demo_per_item: { id: 'replace_demo_per_item', label: 'Demo and disposal per item', type: 'number', unit: '$', money: true, 'default': 0, min: 0, max: 100000, step: 10, source: 'Visitor-entered', tag: 'visitor' },
    replace_install_per_item: { id: 'replace_install_per_item', label: 'Install labor per new item', type: 'number', unit: '$', money: true, 'default': 0, min: 0, max: 100000, step: 10, source: 'Visitor-entered', tag: 'visitor' },
    downtime_days_replace: { id: 'downtime_days_replace', label: 'Days out of service to replace', type: 'number', unit: 'days', 'default': 0, min: 0, max: 365, step: 0.5, source: 'Visitor-entered', tag: 'visitor' },
    downtime_days_reface: { id: 'downtime_days_reface', label: 'Days out of service to reface', type: 'number', unit: 'days', 'default': 0, min: 0, max: 365, step: 0.5, source: 'Visitor-entered', tag: 'visitor' },
    downtime_cost_per_day: { id: 'downtime_cost_per_day', label: 'Cost per out-of-service day', type: 'number', unit: '$', money: true, 'default': 0, min: 0, max: 100000, step: 50, source: 'Visitor-entered', tag: 'visitor' },
    shape: {
      id: 'shape', label: 'Shape', type: 'select', 'default': 'flat',
      options: [{ value: 'flat', label: 'Flat' }, { value: 'curved', label: 'Curved or many edges' }, { value: 'ceiling', label: 'Ceiling' }],
      help: 'Only shapes with a multiplier set by the installer are listed.', source: 'Installer setting'
    }
  };
  var RF_SETTINGS = [
    { id: 'surfacePrepPerItem', label: 'Surface repair or prep per item', unit: '$', money: true, 'default': 0, min: 0, max: 100000, step: 10, tag: 'placeholder', source: 'Placeholder' },
    { id: 'complexityMultiplier', label: 'Shape multiplier', type: 'map', keys: 'shape', unit: 'x', nullable: true, step: 0.05, min: 0.5, max: 10, tag: 'placeholder', source: 'Placeholder, installer to set. Flat = 1.0; a blank shape is not offered.',
      'default': { flat: 1.0, curved: null, ceiling: null } },
    { id: 'refaceLifeYears', label: 'Expected life of the refaced finish', unit: 'years', 'default': null, placeholder: '[enter]', min: 0.5, max: 50, step: 0.5, tag: 'visitor', source: 'Not sourced for Ardor. Leave blank, or enter a life you trust.' },
    { id: 'replaceLifeYears', label: 'Expected life of a new unit', unit: 'years', 'default': null, placeholder: '[enter]', min: 0.5, max: 50, step: 0.5, tag: 'visitor', source: 'Not sourced. Leave blank, or enter a life you trust.' }
  ];

  /* option lists used as keys of the multiplier/fee maps in the assumptions panel */
  var MAP_KEYS = {
    finish: WALL_INPUTS.finish.options, surface: WALL_INPUTS.surface.options, access: WALL_INPUTS.access.options,
    artwork: WALL_INPUTS.artwork.options, shape: RF_INPUTS.shape.options
  };

  var LIMITS_NOTE ='Refacing keeps the existing door, cabinet or panel. It can\'t fix swelling, delamination, water damage or structural problems. 3M requires sound, sealed surfaces.';
  var WHOLE_INSTALLS_NOTE = 'Installs come in whole numbers, so over 10 years a 6-year life costs the same as a 5-year life: two installs either way.';
  var RATE_PLACEHOLDER = 'Rates set after a free on-site measure';

  /* ================================================================ formulas (pure) */
  function settingDefaults(list) {
    var s = {};
    list.forEach(function (d) { s[d.id] = clone(d['default']); });
    return s;
  }
  function multOf(map, key) {
    var v = map && own(map, key) ? num(map[key]) : null;
    return isNum(v) ? v : 1;
  }

  /* wall_cost (size + price mode) — wall-wrap-cost.json spec v1.0 `formulas` */
  function wallCost(v, s) {
    s = s || settingDefaults(WALL_SETTINGS);
    var cov = num(s.usablePrintWidthIn) - num(s.panelOverlapIn);
    var roll = num(s.rollWidthIn), trim = num(s.trimAllowanceFt) || 0;
    var r = {
      walls: [], grossSqFt: 0, netSqFt: 0, panelsTotal: 0, billedMaterialSqFt: 0,
      coveragePerPanelIn: cov, seamSpacingFt: isNum(cov) ? cov / 12 : null,
      coverageOk: isNum(cov) && cov > 0, priceMode: false
    };
    (v.walls || []).forEach(function (w) {
      var wf = num(w && w.width_ft), hf = num(w && w.height_ft);
      var ok = isNum(wf) && isNum(hf) && wf > 0 && hf > 0;
      var area = ok ? wf * hf : 0;
      var panels = ok && r.coverageOk ? ceilG((wf * 12) / cov) : 0;
      var material = ok && isNum(roll) ? panels * (roll / 12) * (hf + trim) : 0;
      r.walls.push({ width_ft: wf, height_ft: hf, area: area, panels: panels, material: material, ok: ok });
      r.grossSqFt += area;
      r.panelsTotal += panels;
      r.billedMaterialSqFt += material;
    });
    var openings = Math.max(0, num(v.openings_sqft) || 0);
    r.openingsCapped = openings > r.grossSqFt;
    r.openingsUsed = Math.min(openings, r.grossSqFt);
    r.netSqFt = Math.max(0, r.grossSqFt - openings);

    var rate = num(s.rate);
    r.priceMode = isNum(rate) && rate > 0 && s.priceModeEnabled !== false;
    if (r.priceMode) {
      r.rate = rate;
      r.mult = multOf(s.finishMultipliers, v.finish) * multOf(s.surfaceMultipliers, v.surface) * multOf(s.accessMultipliers, v.access);
      var fee = s.designFee && own(s.designFee, v.artwork) ? num(s.designFee[v.artwork]) : null;
      r.designPending = !isNum(fee);
      r.designPart = isNum(fee) ? fee : 0;
      r.installedMid = r.netSqFt * rate * r.mult + r.designPart;
      var pct = num(s.rangePct) || 0;
      r.low = roundTo(r.installedMid * (1 - pct / 100), num(s.roundTo));
      r.high = roundTo(r.installedMid * (1 + pct / 100), num(s.roundTo));
    }
    r.messages = wallMessages(v, r);
    return r;
  }

  function wallMessages(v, r) {
    var out = [];
    if (v.surface === 'fresh_paint') { out.push({ text: 'Fresh paint has to cure before film goes on. Avery says to let the final coat dry at least 7 days, and some paints take months to fully cure. We check it at the on-site measure.' }); }
    if (v.surface === 'heavy_texture') { out.push({ text: 'Block, brick and heavy texture need a conformable textured-wall film applied with heat. Very deep grout lines may need the art cut into segments.' }); }
    if (v.surface === 'glass') { out.push({ text: 'Glass takes window film and window graphics. See our window films page.', link_url: '/pages/window-films', link_text: 'window films page' }); }
    if (v.finish === 'exterior' || v.access === 'lift') { out.push({ text: 'Exterior and very tall walls are installed from lifts. We plan access at the on-site measure.' }); }
    if (v.artwork && v.artwork !== 'print_ready') { out.push({ text: 'Design is quoted separately.' }); }
    if (r.panelsTotal > 1 && isNum(r.seamSpacingFt)) {
      out.push({ text: 'Seams land about every ' + fmt(r.seamSpacingFt, 'd1') + ' ft. We plan them to fall in low-visibility spots.' });
    }
    return out;
  }

  /* wall_cost roi_lens mode — roi-of-branded-spaces.json spec */
  function roiLens(v) {
    var pc = num(v.project_cost), y = num(v.years_on_wall), d = num(v.days_open_per_year);
    var p = num(v.people_per_day), e = num(v.employees);
    var r = { ready: isNum(pc) && pc > 0 && isNum(y) && y > 0, costPerYear: null, costPerCalendarDay: null, costPerOpenDay: null, costPerView: null, costPerEmployeePerYear: null };
    if (!r.ready) { return r; }
    r.costPerYear = pc / y;
    r.costPerCalendarDay = pc / (y * 365);
    if (isNum(d) && d > 0) { r.costPerOpenDay = pc / (y * d); }
    if (isNum(d) && d > 0 && isNum(p) && p > 0) { r.costPerView = pc / (y * d * p); }
    if (isNum(e) && e > 0) { r.costPerEmployeePerYear = r.costPerYear / e; }
    return r;
  }

  /* wall_vs_paint_wallpaper — one option */
  var OPTIONAL_FIELDS = ['removal_cost_per_sqft', 'maintenance_per_sqft_per_year', 'downtime_days'];
  function vsOption(o, ctx) {
    var inst = num(o.install_cost_per_sqft), life = num(o.life_years);
    var res = { computed: false, notIncluded: [] };
    if (!isNum(inst) || !isNum(life) || life <= 0 || !(ctx.area > 0) || !(ctx.horizon > 0)) { return res; }
    OPTIONAL_FIELDS.forEach(function (k) { if (!isNum(num(o[k]))) { res.notIncluded.push(k); } });
    var rem = num(o.removal_cost_per_sqft) || 0, mnt = num(o.maintenance_per_sqft_per_year) || 0, dd = num(o.downtime_days) || 0;
    res.computed = true;
    res.cycles = ceilG(ctx.horizon / life);
    res.installTotal = res.cycles * ctx.area * inst;
    res.removals = res.cycles - 1 + (ctx.includeFinal ? 1 : 0);
    res.removalTotal = res.removals * ctx.area * rem;
    res.maintenanceTotal = ctx.horizon * ctx.area * mnt;
    res.downtimeTotal = res.cycles * dd * ctx.downtimeCost;
    res.total = res.installTotal + res.removalTotal + res.maintenanceTotal + res.downtimeTotal;
    res.perYear = res.total / ctx.horizon;
    res.perSqFtPerYear = res.perYear / ctx.area;
    return res;
  }

  function wallVs(v, opts, visible) {
    var ctx = {
      area: num(v.area_sqft), horizon: num(v.horizon_years),
      includeFinal: !!v.include_final_removal, downtimeCost: num(v.downtime_cost_per_day) || 0
    };
    visible = visible || ['paint', 'wallpaper', 'wall_wrap'];
    var r = { options: {}, order: visible.slice(), lowest: null, breakEvenLife: null, breakEvenStatus: 'needs' };
    visible.forEach(function (id) { r.options[id] = vsOption(opts[id] || {}, ctx); });
    visible.forEach(function (id) {
      var o = r.options[id];
      if (o.computed && (!r.lowest || o.perYear < r.options[r.lowest].perYear - 1e-9)) { r.lowest = id; }
    });
    var wrap = opts.wall_wrap;
    var others = visible.filter(function (id) { return id !== 'wall_wrap' && r.options[id].computed; });
    if (wrap && visible.indexOf('wall_wrap') > -1 && isNum(num(wrap.install_cost_per_sqft)) && others.length && ctx.horizon > 0 && ctx.area > 0) {
      var lowestOther = Math.min.apply(null, others.map(function (id) { return r.options[id].perYear; }));
      r.breakEvenStatus = 'none';
      for (var L = 1; L <= Math.floor(ctx.horizon); L++) {
        var t = clone(wrap);
        t.life_years = L;
        var tr = vsOption(t, ctx);
        if (tr.computed && tr.perYear <= lowestOther + 1e-9) { r.breakEvenLife = L; r.breakEvenStatus = 'ok'; break; }
      }
      r.breakEvenTarget = lowestOther;
    }
    return r;
  }

  /* reface_vs_replace */
  function reface(v, s) {
    s = s || settingDefaults(RF_SETTINGS);
    var q = num(v.quantity), a = num(v.area_per_item_sqft), rc = num(v.replace_cost_per_item);
    var demo = num(v.replace_demo_per_item) || 0, inst = num(v.replace_install_per_item) || 0;
    var ddRep = num(v.downtime_days_replace) || 0, ddRef = num(v.downtime_days_reface) || 0, dc = num(v.downtime_cost_per_day) || 0;
    var cxMap = s.complexityMultiplier || {};
    var cx = num(cxMap[v.shape || 'flat']);
    if (!isNum(cx) || cx <= 0) { cx = 1; }
    var prep = num(s.surfacePrepPerItem) || 0;
    var rate = num(s.refaceCostPerSqFt);
    var r = { valid: isNum(q) && q > 0 && isNum(a) && a > 0, complexity: cx, replaceTotal: null, refaceTotal: null, savings: null, savingsPct: null, breakEvenPrice: null, annualizedReface: null, annualizedReplace: null, rateSet: isNum(rate) && rate > 0 };
    if (!r.valid) { return r; }
    if (isNum(rc)) { r.replaceTotal = q * (rc + demo + inst) + ddRep * dc; }
    if (r.rateSet) { r.refaceTotal = q * (a * rate * cx + prep) + ddRef * dc; }
    if (r.replaceTotal != null && r.refaceTotal != null) {
      r.savings = r.replaceTotal - r.refaceTotal;
      r.savingsPct = r.replaceTotal > 0 ? r.savings / r.replaceTotal : null;
    }
    if (r.replaceTotal != null) {
      r.breakEvenPrice = (r.replaceTotal - ddRef * dc - q * prep) / (q * a * cx);
    }
    var lf = num(s.refaceLifeYears), lp = num(s.replaceLifeYears);
    if (isNum(lf) && lf > 0 && isNum(lp) && lp > 0) {
      if (r.refaceTotal != null) { r.annualizedReface = r.refaceTotal / lf; }
      if (r.replaceTotal != null) { r.annualizedReplace = r.replaceTotal / lp; }
    }
    return r;
  }

  /* ================================================================ formatting */
  var NF = {};
  function nf(key, opts) {
    if (!NF[key]) {
      try { NF[key] = new Intl.NumberFormat('en-US', opts); } catch (e) { NF[key] = new Intl.NumberFormat('en-US'); }
    }
    return NF[key];
  }
  var DASH = '–';
  function fmt(x, f) {
    if (!isNum(x)) { return DASH; }
    switch (f) {
      case 'money0': return nf('m0', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(x);
      case 'money2': return nf('m2', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(x);
      case 'money3': return nf('m3', { style: 'currency', currency: 'USD', minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(x);
      case 'd1': return nf('d1', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(x);
      case 'd2': return nf('d2', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(x);
      case 'int': return nf('i', { maximumFractionDigits: 0 }).format(Math.round(x));
      case 'pct0': return nf('p0', { style: 'percent', maximumFractionDigits: 0 }).format(x);
      default: return nf('n', { maximumFractionDigits: 2 }).format(x);
    }
  }
  /* 18.333 -> 18′ 4″ (nearest inch) */
  function feetInches(ft) {
    if (!isNum(ft)) { return DASH; }
    var total = Math.round(ft * 12), f = Math.floor(total / 12), i = total - f * 12;
    return f + '′ ' + i + '″';
  }
  function plural(n, one, many) { return n === 1 ? one : many; }
  function wallLetter(i) { return String.fromCharCode(65 + (i % 26)); }

  /* ================================================================ models */
  function parseConfig(raw) {
    if (!raw) { return {}; }
    try {
      var c = JSON.parse(raw);
      return (c && typeof c === 'object' && !Array.isArray(c)) ? c : {};
    } catch (e) {
      if (root.console) { root.console.warn('[CWV calculator] config_json is not valid JSON and was ignored.', e); }
      return {};
    }
  }
  function positiveRate(cfg, keys) {
    for (var i = 0; i < keys.length; i++) {
      var r = num(cfg[keys[i]]);
      if (isNum(r) && r > 0) { return r; }
    }
    return null;
  }
  /* settings: defaults < cfg.settings < top-level keys of cfg */
  function mergeSettings(list, cfg) {
    var s = settingDefaults(list);
    var src = [cfg.settings || {}, cfg];
    list.forEach(function (d) {
      src.forEach(function (o) {
        if (!own(o, d.id)) { return; }
        if (d.type === 'map') {
          if (o[d.id] && typeof o[d.id] === 'object') {
            each(o[d.id], function (val, k) { s[d.id][k] = val === null ? null : num(val); });
          }
        } else {
          var n = o[d.id] === null ? null : num(o[d.id]);
          if (n !== null || d.nullable || d['default'] === null) { s[d.id] = n; }
        }
      });
    });
    return s;
  }
  function inputDefaults(map, cfg) {
    var v = {}, d = (cfg && cfg.defaults) || {};
    each(map, function (inp, id) {
      v[id] = clone(inp['default']);
      var over = own(d, id) ? d[id] : (own(cfg || {}, id) ? cfg[id] : undefined);
      if (over === undefined) { return; }
      if (inp.type === 'repeater') {
        if (Array.isArray(over) && over.length) {
          v[id] = over.slice(0, inp.max_items).map(function (w) {
            var o = {};
            inp.fields.forEach(function (f) { var n = num(w && w[f.id]); o[f.id] = isNum(n) ? clamp(n, f.min, f.max) : f['default']; });
            return o;
          });
        }
      } else if (inp.type === 'select') {
        if (inp.options.some(function (o) { return o.value === over; })) { v[id] = over; }
      } else if (inp.type === 'toggle') {
        v[id] = !!over;
      } else {
        var n = over === null ? null : num(over);
        v[id] = n === null ? null : clamp(n, inp.min, inp.max);
      }
    });
    return v;
  }

  function buildModel(id, cfg, mode) {
    cfg = cfg || {};
    var m = { id: id, cfg: cfg };
    if (id === 'wall_cost') {
      m.mode = { full: 1, teaser: 1, roi_lens: 1 }[cfg.mode] ? cfg.mode : ({ full: 1, teaser: 1, roi_lens: 1 }[mode] ? mode : 'full');
      m.settings = mergeSettings(WALL_SETTINGS, cfg);
      m.settings.rate = positiveRate(cfg, ['rate_per_sqft', 'baseRatePerSqFt']);
      m.settings.priceModeEnabled = !(cfg.priceModeEnabled === false || cfg.price_mode === false);
      m.priceMode = isNum(m.settings.rate) && m.settings.priceModeEnabled;
      var inputs = clone(WALL_INPUTS);
      if (m.mode === 'roi_lens') { each(clone(LENS_INPUTS), function (inp, k) { inputs[k] = inp; }); }
      m.inputs = inputs;
      m.values = inputDefaults(inputs, cfg);
      if (m.mode === 'teaser') { m.values.walls = m.values.walls.slice(0, 1); }
    } else if (id === 'wall_vs_paint_wallpaper') {
      m.inputs = clone(VS_INPUTS);
      m.values = inputDefaults(m.inputs, cfg);
      m.options = clone(VS_OPTIONS);
      m.opt = {};
      var co = cfg.options || {};
      var rate = positiveRate(cfg, ['rate_per_sqft', 'baseRatePerSqFt']);
      m.options.forEach(function (o) {
        m.opt[o.id] = {};
        o.assumptions.forEach(function (a) {
          var val = clone(a['default']);
          if (o.id === 'wall_wrap' && a.id === 'install_cost_per_sqft' && isNum(rate)) { val = rate; a.tag = 'sourced'; a.source = 'Ardor\'s installed rate per sq ft (set by Ardor). Change it to test your own number.'; }
          if (co[o.id] && own(co[o.id], a.id)) { var n = co[o.id][a.id] === null ? null : num(co[o.id][a.id]); val = n === null ? null : clamp(n, a.min, a.max); }
          m.opt[o.id][a.id] = val;
        });
        if (co[o.id] && own(co[o.id], 'visible')) { o.visible = !!co[o.id].visible; }
      });
      if (cfg.defaults && own(cfg.defaults, 'show_mural')) { m.options[3].visible = !!cfg.defaults.show_mural; }
    } else if (id === 'reface_vs_replace') {
      m.inputs = clone(RF_INPUTS);
      m.settings = mergeSettings(RF_SETTINGS, cfg);
      m.settings.refaceCostPerSqFt = positiveRate(cfg, ['reface_rate_per_sqft', 'refaceCostPerSqFt', 'rate_per_sqft']);
      var cm = m.settings.complexityMultiplier;
      m.inputs.shape.options = m.inputs.shape.options.filter(function (o) { return o.value === 'flat' || isNum(num(cm[o.value])); });
      m.values = inputDefaults(m.inputs, cfg);
    } else {
      return null;
    }
    m.defaultValues = clone(m.values);
    m.defaultSettings = clone(m.settings || null);
    m.defaultOpt = clone(m.opt || null);
    return m;
  }

  /* ================================================================ DOM */
  var uid = 0;
  function h(tag, attrs, kids) {
    var el = document.createElement(tag);
    each(attrs, function (v, k) {
      if (v == null || v === false) { return; }
      if (k === 'class') { el.className = v; }
      else if (k === 'text') { el.textContent = v; }
      else if (k === 'html') { el.innerHTML = v; }
      else if (k.slice(0, 2) === 'on' && typeof v === 'function') { el.addEventListener(k.slice(2), v); }
      else { el.setAttribute(k, v === true ? '' : v); }
    });
    (kids || []).forEach(function (c) {
      if (c == null || c === false) { return; }
      el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return el;
  }
  var SVGNS = 'http://www.w3.org/2000/svg';
  function s(tag, attrs) {
    var el = document.createElementNS(SVGNS, tag);
    each(attrs, function (v, k) { if (v != null) { el.setAttribute(k, v); } });
    return el;
  }
  function icon(d, size) {
    var g = s('svg', { viewBox: '0 0 24 24', width: size || 14, height: size || 14, 'aria-hidden': 'true', focusable: 'false', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' });
    g.appendChild(s('path', { d: d }));
    return g;
  }
  var I = { plus: 'M12 5v14M5 12h14', minus: 'M5 12h14', close: 'M6 6l12 12M18 6 6 18', reset: 'M3 12a9 9 0 1 0 3-6.7M3 4v5h5', arrow: 'M5 12h14M13 6l6 6-6 6', down: 'M12 5v14M6 13l6 6 6-6' };
  function reducedMotion() {
    try { return root.matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.getAttribute('data-shift') === 'off'; }
    catch (e) { return false; }
  }
  function tagEl(tag) { return tag && TAGS[tag] ? h('span', { class: 'cf-tag cf-tag--' + tag, text: TAGS[tag] }) : null; }
  function unitText(u) {
    if (!u || u === '$') { return ''; }
    return u === 'sq ft' ? 'SQ FT' : u.toUpperCase();
  }
  function stepDecimals(step) {
    var str = String(step || 1);
    return str.indexOf('.') < 0 ? 0 : str.length - str.indexOf('.') - 1;
  }

  /* ================================================================ Calc */
  function Calc(mount) {
    this.mount = mount;
    this.id = mount.getAttribute('data-cwv-calc');
    this.section = mount.closest('[data-cwv-calc-section]') || mount.parentNode;
    this.uid = 'cw' + (++uid);
    var cfgEl = this.section.querySelector('[data-cwv-calc-config]');
    this.cfg = parseConfig(cfgEl ? (cfgEl.textContent || '').trim() : '');
    this.m = buildModel(this.id, this.cfg, mount.getAttribute('data-mode'));
    if (!this.m) {
      mount.innerHTML = '';
      mount.appendChild(h('p', { class: 'calc-noscript', text: 'Unknown calculator "' + this.id + '". Pick one in the section settings.' }));
      return;
    }
    this.f = {};
    this.out = {};
    this.selWall = 0;
    this.render();
    this.update(true);
  }

  Calc.prototype.render = function () {
    var self = this, m = this.m, mount = this.mount;
    ['.calc-skeleton', '[data-calc-ssr]'].forEach(function (sel) {
      var x = mount.querySelector(sel);
      if (x) { x.parentNode.removeChild(x); }
    });
    this.app = h('div', { class: 'calc-app calc-app--' + m.id + (m.mode ? ' calc-app--' + m.mode : '') + (m.priceMode ? ' is-price' : '') });
    if (m.id === 'wall_cost') { this.renderWall(); }
    else if (m.id === 'wall_vs_paint_wallpaper') { this.renderVs(); }
    else { this.renderReface(); }
    this.live = h('p', { class: 'cwv-sr-only', 'aria-live': 'polite' });
    this.app.appendChild(this.live);
    mount.appendChild(this.app);

    /* price-mode intro swap (section renders both when set) */
    var introP = this.section.querySelector('[data-calc-intro-price]');
    var intro = this.section.querySelector('[data-calc-intro]');
    if (introP && m.priceMode) { introP.hidden = false; if (intro) { intro.hidden = true; } }

    mount.classList.add('is-ready');
    this.section.classList.add('is-ready');
    void self;
  };

  /* -------------------------------------------------- generic fields */
  Calc.prototype.fid = function (key) { return this.uid + '-' + String(key).replace(/[^\w-]/g, '_'); };

  /* get/set by path: ['values','openings_sqft'] | ['values','walls',0,'width_ft'] | ['settings','rangePct'] | ['opt','paint','life_years'] | ['settings','finishMultipliers','floor'] */
  Calc.prototype.get = function (path) {
    var o = this.m;
    for (var i = 0; i < path.length; i++) { if (o == null) { return undefined; } o = o[path[i]]; }
    return o;
  };
  Calc.prototype.put = function (path, val) {
    var o = this.m;
    for (var i = 0; i < path.length - 1; i++) { o = o[path[i]]; }
    o[path[path.length - 1]] = val;
  };

  Calc.prototype.numberField = function (def, path, opts) {
    var self = this;
    opts = opts || {};
    var key = path.join('.');
    var id = this.fid(key);
    var wrap = h('div', { class: 'cf cf--num' + (opts.compact ? ' cf--compact' : '') + (def.money ? ' cf--money' : ''), 'data-f': key });
    var top = h('div', { class: 'cf-top' }, [
      h('label', { class: 'cf-label', for: id, text: opts.label || def.label }),
      opts.noTag ? null : tagEl(def.tag)
    ]);
    wrap.appendChild(top);
    var input = h('input', {
      type: 'number', id: id, class: 'cf-input', inputmode: 'decimal',
      min: isNum(def.min) ? def.min : null, max: isNum(def.max) ? def.max : null,
      step: isNum(def.step) ? def.step : 'any', placeholder: def.placeholder || null, autocomplete: 'off'
    });
    var minus = h('button', { type: 'button', class: 'cf-step', 'aria-label': 'Decrease ' + (opts.label || def.label), tabindex: '-1' }, [icon(I.minus, 14)]);
    var plus = h('button', { type: 'button', class: 'cf-step', 'aria-label': 'Increase ' + (opts.label || def.label), tabindex: '-1' }, [icon(I.plus, 14)]);
    var box = h('div', { class: 'cf-box' }, [
      minus,
      def.money ? h('span', { class: 'cf-pre', 'aria-hidden': 'true', text: '$' }) : null,
      input,
      unitText(def.unit) ? h('span', { class: 'cf-unit', 'aria-hidden': 'true', text: unitText(def.unit) }) : null,
      plus
    ]);
    wrap.appendChild(box);
    var msg = h('p', { class: 'cf-msg', id: id + '-msg', hidden: true });
    wrap.appendChild(msg);
    var described = [id + '-msg'];
    if (def.help && !opts.noHelp) { wrap.appendChild(h('p', { class: 'cf-help', id: id + '-help', text: def.help })); described.push(id + '-help'); }
    if (def.source && !opts.noSource) { wrap.appendChild(h('p', { class: 'cf-src', id: id + '-src' }, [h('span', { class: 'cf-src-k', text: 'Source' }), def.source])); described.push(id + '-src'); }
    input.setAttribute('aria-describedby', described.join(' '));
    var nullable = !!(def['default'] === null || def.nullable || opts.nullable);
    var f = { def: def, path: path, wrap: wrap, input: input, msg: msg, nullable: nullable };
    this.f[key] = f;

    function commit(raw, fromTyping) {
      raw = String(raw).trim();
      if (raw === '') {
        if (nullable) { self.put(path, null); msg.hidden = true; }
        else { msg.hidden = false; msg.textContent = 'Enter a number.'; return; }
      } else {
        var x = parseFloat(raw);
        if (!isNum(x)) { return; }
        var c = clamp(x, def.min, def.max);
        if (c !== x) {
          msg.hidden = false;
          msg.textContent = 'Use a number from ' + fmt(def.min) + ' to ' + fmt(def.max) + (def.unit && def.unit !== '$' ? ' ' + def.unit : '') + '. Using ' + fmt(c) + '.';
        } else { msg.hidden = true; }
        self.put(path, c);
        if (!fromTyping) { input.value = String(c); }
      }
      self.markEmpty(f);
      self.update();
    }
    input.addEventListener('input', function () { commit(input.value, true); });
    input.addEventListener('change', function () { self.syncField(f); });
    input.addEventListener('blur', function () { self.syncField(f); });
    function bump(dir) {
      var cur = num(self.get(path));
      var stp = def.step || 1;
      var base = isNum(cur) ? cur : (isNum(def.min) ? Math.max(def.min, 0) : 0);
      var next = isNum(cur) ? base + dir * stp : base;
      next = parseFloat(clamp(next, def.min, def.max).toFixed(stepDecimals(stp) + 2));
      self.put(path, next);
      msg.hidden = true;
      self.syncField(f);
      self.update();
    }
    minus.addEventListener('click', function () { bump(-1); });
    plus.addEventListener('click', function () { bump(1); });
    this.syncField(f);
    return wrap;
  };

  Calc.prototype.markEmpty = function (f) {
    var v = this.get(f.path);
    f.wrap.classList.toggle('is-empty', !!(f.nullable && !isNum(num(v))));
  };
  Calc.prototype.syncField = function (f) {
    var v = this.get(f.path);
    if (f.input) {
      /* never rewrite the field the visitor is typing in; blur/change re-syncs it */
      if (document.activeElement !== f.input) {
        var n = num(v);
        f.input.value = isNum(n) ? String(n) : '';
      }
      this.markEmpty(f);
    }
    if (f.sel) { f.sel.value = v == null ? '' : String(v); }
    if (f.radios) { f.radios.forEach(function (r) { r.checked = r.value === String(v); }); }
    if (f.cb) { f.cb.checked = !!v; }
    if (f.son) { this.syncSon(f); }
  };

  Calc.prototype.selectField = function (def, path, opts) {
    var self = this;
    opts = opts || {};
    var key = path.join('.'), id = this.fid(key);
    var chips = !opts.forceSelect && def.options.length <= 4 && def.options.every(function (o) { return String(o.label).length <= 30; });
    var wrap, f = { def: def, path: path };
    if (chips) {
      wrap = h('fieldset', { class: 'cf cf--chips', 'data-f': key });
      wrap.appendChild(h('legend', { class: 'cf-label', text: def.label }));
      var row = h('div', { class: 'cf-chips' });
      f.radios = [];
      def.options.forEach(function (o, i) {
        var r = h('input', { type: 'radio', class: 'cf-chip-input', name: id, id: id + '-' + i, value: String(o.value) });
        r.addEventListener('change', function () { if (r.checked) { self.put(path, o.value); self.update(); } });
        f.radios.push(r);
        row.appendChild(h('label', { class: 'cf-chip', for: id + '-' + i }, [r, h('span', { class: 'cf-chip-ui', text: o.label })]));
      });
      wrap.appendChild(row);
    } else {
      wrap = h('div', { class: 'cf cf--select', 'data-f': key });
      wrap.appendChild(h('div', { class: 'cf-top' }, [h('label', { class: 'cf-label', for: id, text: def.label })]));
      var sel = h('select', { id: id, class: 'cf-select' });
      def.options.forEach(function (o) { sel.appendChild(h('option', { value: String(o.value), text: o.label })); });
      sel.addEventListener('change', function () {
        var o = def.options.filter(function (x) { return String(x.value) === sel.value; })[0];
        self.put(path, o ? o.value : sel.value);
        self.update();
      });
      wrap.appendChild(h('div', { class: 'cf-select-wrap' }, [sel]));
      f.sel = sel;
    }
    if (def.help) { wrap.appendChild(h('p', { class: 'cf-help', text: def.help })); }
    if (def.source) { wrap.appendChild(h('p', { class: 'cf-src' }, [h('span', { class: 'cf-src-k', text: 'Source' }), def.source])); }
    f.wrap = wrap;
    this.f[key] = f;
    this.syncField(f);
    return wrap;
  };

  Calc.prototype.toggleField = function (def, path) {
    var self = this, key = path.join('.'), id = this.fid(key);
    var cb = h('input', { type: 'checkbox', role: 'switch', id: id, class: 'cf-switch-input' });
    var wrap = h('div', { class: 'cf cf--toggle', 'data-f': key }, [
      h('label', { class: 'cf-switch', for: id }, [cb, h('span', { class: 'cf-switch-ui', 'aria-hidden': 'true' }), h('span', { class: 'cf-label', text: def.label })]),
      def.help ? h('p', { class: 'cf-help', text: def.help }) : null
    ]);
    cb.addEventListener('change', function () { self.put(path, cb.checked); self.update(); });
    var f = { def: def, path: path, wrap: wrap, cb: cb };
    this.f[key] = f;
    this.syncField(f);
    return wrap;
  };

  /* select_or_number: radio cards for sourced scenarios + "Custom" with a number */
  Calc.prototype.sonField = function (def, path) {
    var self = this, key = path.join('.'), id = this.fid(key);
    var wrap = h('fieldset', { class: 'cf cf--son', 'data-f': key });
    wrap.appendChild(h('legend', { class: 'cf-label' }, [def.label, ' ', tagEl(def.tag || 'sourced')]));
    var list = h('div', { class: 'cf-son' });
    var f = { def: def, path: path, wrap: wrap, son: true, cards: [] };
    def.options.forEach(function (o, i) {
      var parts = String(o.label).split(': ');
      var r = h('input', { type: 'radio', class: 'cf-son-input', name: id, id: id + '-' + i, value: String(o.value) });
      r.addEventListener('change', function () { if (r.checked) { self.put(path, o.value); self.syncSon(f); self.update(); } });
      f.cards.push({ r: r, v: o.value });
      list.appendChild(h('label', { class: 'cf-son-card', for: id + '-' + i }, [
        r,
        h('span', { class: 'cf-son-ui' }, [
          h('span', { class: 'cf-son-v', text: parts[0] }),
          h('span', { class: 'cf-son-t', text: parts.slice(1).join(': ') }),
          o.source ? h('span', { class: 'cf-son-s', text: o.source }) : null
        ])
      ]));
    });
    var cr = h('input', { type: 'radio', class: 'cf-son-input', name: id, id: id + '-c', value: 'custom' });
    var cn = h('input', { type: 'number', class: 'cf-input cf-son-num', id: id + '-cn', min: def.min, max: def.max, step: def.step, inputmode: 'decimal', 'aria-label': def.label + ', your own number of years' });
    f.custom = { r: cr, n: cn };
    list.appendChild(h('div', { class: 'cf-son-card cf-son-card--custom' }, [
      cr,
      h('label', { class: 'cf-son-ui', for: id + '-c' }, [h('span', { class: 'cf-son-v', text: 'Your own' }), h('span', { class: 'cf-son-t', text: 'Type a number of years' })]),
      h('span', { class: 'cf-box cf-box--mini' }, [cn, h('span', { class: 'cf-unit', 'aria-hidden': 'true', text: unitText(def.unit) })])
    ]));
    cr.addEventListener('change', function () { if (cr.checked) { cn.focus(); if (isNum(num(cn.value))) { self.put(path, clamp(num(cn.value), def.min, def.max)); self.update(); } } });
    cn.addEventListener('input', function () {
      var x = num(cn.value);
      if (!isNum(x)) { return; }
      cr.checked = true;
      self.put(path, clamp(x, def.min, def.max));
      self.update();
    });
    cn.addEventListener('blur', function () { self.syncSon(f); });
    wrap.appendChild(list);
    if (def.help) { wrap.appendChild(h('p', { class: 'cf-help', text: def.help })); }
    this.f[key] = f;
    this.syncSon(f);
    return wrap;
  };
  Calc.prototype.syncSon = function (f) {
    var v = num(this.get(f.path)), hit = false;
    f.cards.forEach(function (c) { var on = v === c.v; c.r.checked = on; if (on) { hit = true; } });
    f.custom.r.checked = !hit;
    if (!hit && document.activeElement !== f.custom.n) { f.custom.n.value = isNum(v) ? String(v) : ''; }
    if (hit && document.activeElement !== f.custom.n) { f.custom.n.value = ''; }
  };

  Calc.prototype.field = function (def, path, opts) {
    if (def.type === 'select') { return this.selectField(def, path, opts); }
    if (def.type === 'toggle') { return this.toggleField(def, path, opts); }
    if (def.type === 'select_or_number') { return this.sonField(def, path, opts); }
    return this.numberField(def, path, opts);
  };

  Calc.prototype.step = function (n, title, kids, cls) {
    return h('fieldset', { class: 'calc-step' + (cls ? ' ' + cls : '') }, [
      h('legend', { class: 'calc-step-k' }, [h('span', { class: 'calc-step-n', text: 'STEP ' + n }), h('span', { class: 'calc-step-t', text: title })])
    ].concat(kids));
  };

  Calc.prototype.ctaRow = function () {
    var cta = this.section.querySelector('[data-calc-cta]');
    var self = this;
    var reset = h('button', { type: 'button', class: 'calc-reset', onclick: function () { self.reset(); } }, [icon(I.reset, 14), 'Reset']);
    var row = h('div', { class: 'calc-actions' });
    if (cta) { row.appendChild(cta); }
    row.appendChild(reset);
    return row;
  };

  Calc.prototype.reset = function () {
    var m = this.m, self = this;
    m.values = clone(m.defaultValues);
    if (m.defaultSettings) { var rate = m.settings.rate, rr = m.settings.refaceCostPerSqFt; m.settings = clone(m.defaultSettings); if (rate !== undefined) { m.settings.rate = rate; } if (rr !== undefined) { m.settings.refaceCostPerSqFt = rr; } }
    if (m.defaultOpt) { m.opt = clone(m.defaultOpt); }
    this.selWall = 0;
    this.lensTouched = false;
    each(this.f, function (f) { if (f.msg) { f.msg.hidden = true; } });
    if (this.renderWalls) { this.renderWalls(); }
    each(this.f, function (f) { self.syncField(f); });
    this.update();
    this.say('Reset to the default numbers.');
  };

  Calc.prototype.say = function (txt) {
    var live = this.live;
    clearTimeout(this.sayT);
    this.sayT = setTimeout(function () { live.textContent = txt; }, 650);
  };

  /* -------------------------------------------------- assumptions panel */
  Calc.prototype.assumptions = function (list, title, filter) {
    var self = this, m = this.m;
    var det = h('details', { class: 'calc-assume' });
    var shown = list.filter(function (d) { return !filter || filter(d); });
    det.appendChild(h('summary', { class: 'calc-assume-sum' }, [
      h('span', { class: 'calc-assume-k' }, [h('span', { class: 'calc-assume-dot', 'aria-hidden': 'true' }), title || 'How we estimate']),
      h('span', { class: 'calc-assume-n', text: shown.length + ' editable ' + plural(shown.length, 'assumption', 'assumptions') })
    ]));
    var body = h('div', { class: 'calc-assume-body' });
    var grid = h('div', { class: 'calc-assume-grid' });
    shown.forEach(function (d) {
      if (d.type === 'map') {
        var keys = MAP_KEYS[d.keys] || [];
        var box = h('fieldset', { class: 'cf cf--map' }, [h('legend', { class: 'cf-label' }, [d.label, ' ', tagEl(d.tag)])]);
        var rows = h('div', { class: 'cf-map' });
        keys.forEach(function (o) {
          rows.appendChild(self.numberField({ label: o.label, unit: d.unit, money: d.money, min: d.min, max: d.max, step: d.step, 'default': d['default'][o.value], nullable: d.nullable, placeholder: d.nullable ? 'Quoted separately' : null },
            ['settings', d.id, o.value], { compact: true, noTag: true, noHelp: true, noSource: true, nullable: d.nullable }));
        });
        box.appendChild(rows);
        box.appendChild(h('p', { class: 'cf-src' }, [h('span', { class: 'cf-src-k', text: 'Source' }), d.source]));
        grid.appendChild(box);
      } else {
        grid.appendChild(self.numberField(d, ['settings', d.id], { compact: true }));
      }
    });
    body.appendChild(grid);
    det.appendChild(body);
    this.assumeBody = body;
    return det;
  };

  /* ================================================================ wall_cost UI */
  Calc.prototype.renderWall = function () {
    var self = this, m = this.m, app = this.app;
    var ui = h('div', { class: 'calc-ui calc-ui--wall' });
    var visual = h('div', { class: 'calc-visual' });
    var inputs = h('div', { class: 'calc-inputs' });

    if (m.mode !== 'roi_lens' || m.priceMode) {
      visual.appendChild(this.stage());
    }
    if (m.mode === 'roi_lens') {
      visual.appendChild(this.lensResults());
    } else {
      visual.appendChild(this.wallResults());
    }

    var n = 1;
    if (m.mode === 'roi_lens') {
      inputs.appendChild(this.step(n++, 'Project cost', [this.field(m.inputs.project_cost, ['values', 'project_cost'])]));
      inputs.appendChild(this.step(n++, 'Time on the wall', [this.field(m.inputs.years_on_wall, ['values', 'years_on_wall']), this.field(m.inputs.days_open_per_year, ['values', 'days_open_per_year'])]));
      inputs.appendChild(this.step(n++, 'People', [this.field(m.inputs.people_per_day, ['values', 'people_per_day']), this.field(m.inputs.employees, ['values', 'employees'])]));
      if (m.priceMode) {
        var det = h('details', { class: 'calc-step calc-step--more' }, [
          h('summary', { class: 'calc-step-k' }, [h('span', { class: 'calc-step-n', text: 'OPTIONAL' }), h('span', { class: 'calc-step-t', text: 'Size the wall to prefill the cost' })])
        ]);
        det.appendChild(this.wallsBlock());
        det.appendChild(this.field(m.inputs.finish, ['values', 'finish']));
        det.appendChild(this.field(m.inputs.surface, ['values', 'surface']));
        det.appendChild(this.field(m.inputs.access, ['values', 'access']));
        det.appendChild(this.field(m.inputs.artwork, ['values', 'artwork']));
        inputs.appendChild(det);
      }
    } else if (m.mode === 'teaser') {
      inputs.appendChild(this.step(n++, 'Wall size', [this.wallsBlock()]));
      inputs.appendChild(this.step(n++, 'Film and finish', [this.field(m.inputs.finish, ['values', 'finish'], { forceSelect: true })]));
    } else {
      inputs.appendChild(this.step(n++, 'Wall size', [this.wallsBlock()]));
      inputs.appendChild(this.step(n++, 'Openings', [this.field(m.inputs.openings_sqft, ['values', 'openings_sqft'])]));
      inputs.appendChild(this.step(n++, 'Film and surface', [
        this.field(m.inputs.finish, ['values', 'finish']),
        this.field(m.inputs.surface, ['values', 'surface']),
        this.field(m.inputs.access, ['values', 'access']),
        this.field(m.inputs.artwork, ['values', 'artwork'])
      ]));
    }
    /* phone: sticky mini readout */
    this.mini = h('div', { class: 'calc-mini', 'aria-hidden': 'true' }, [h('span', { class: 'calc-mini-k' }), h('span', { class: 'calc-mini-v' })]);
    var go = h('a', { class: 'calc-mini-go', href: '#' + this.uid + '-out', tabindex: '-1' }, ['Results', icon(I.down, 13)]);
    go.addEventListener('click', function (e) {
      e.preventDefault();
      var t = document.getElementById(self.uid + '-out');
      if (t) { t.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' }); }
    });
    this.mini.appendChild(go);
    inputs.appendChild(this.mini);
    inputs.appendChild(this.ctaRow());

    ui.appendChild(visual);
    ui.appendChild(inputs);
    app.appendChild(ui);

    if (m.mode !== 'teaser') {
      var title = m.mode === 'roi_lens' ? 'Size assumptions' : 'How we estimate';
      if (m.mode !== 'roi_lens' || m.priceMode) {
        app.appendChild(this.assumptions(WALL_SETTINGS, title, function (d) { return !d.price || m.priceMode; }));
        this.assumeBody.appendChild(h('p', { class: 'calc-assume-rate' }, [
          h('span', { class: 'cf-tag cf-tag--' + (m.priceMode ? 'sourced' : 'missing'), text: m.priceMode ? 'Set by Ardor' : 'Not set' }),
          ' Ardor installed price per sq ft, smooth wall: ',
          h('b', { text: m.priceMode ? fmt(m.settings.rate, 'money2') + ' per sq ft' : RATE_PLACEHOLDER + '.' }),
          m.priceMode ? '' : ' Dollar estimates stay off until Ardor sets a rate, so this tool shows size only.'
        ]));
      }
    }
  };

  Calc.prototype.wallsBlock = function () {
    var self = this, m = this.m, def = m.inputs.walls;
    var wrap = h('div', { class: 'cf-walls', role: 'group', 'aria-label': def.label });
    this.wallList = h('ol', { class: 'cw-list' });
    wrap.appendChild(this.wallList);
    if (m.mode !== 'teaser') {
      this.addWallBtn = h('button', { type: 'button', class: 'cw-add' }, [icon(I.plus, 14), def.add_label]);
      this.addWallBtn.addEventListener('click', function () {
        var walls = m.values.walls;
        if (walls.length >= def.max_items) { return; }
        var last = walls[walls.length - 1] || { width_ft: 12, height_ft: 9 };
        walls.push({ width_ft: last.width_ft, height_ft: last.height_ft });
        self.selWall = walls.length - 1;
        self.renderWalls();
        self.update();
        var inp = self.wallList.querySelectorAll('.cf-input');
        if (inp.length) { inp[inp.length - 2].focus(); }
        self.say('Wall ' + wallLetter(self.selWall) + ' added.');
      });
      wrap.appendChild(this.addWallBtn);
      wrap.appendChild(h('p', { class: 'cf-help', text: def.help + ' Up to ' + def.max_items + ' walls.' }));
    }
    this.renderWalls();
    return wrap;
  };

  Calc.prototype.renderWalls = function () {
    if (!this.wallList) { return; }
    var self = this, m = this.m, def = m.inputs.walls;
    var walls = m.values.walls;
    Object.keys(this.f).forEach(function (k) { if (k.indexOf('values.walls.') === 0) { delete self.f[k]; } });
    this.wallList.innerHTML = '';
    walls.forEach(function (w, i) {
      var li = h('li', { class: 'cw-row' + (i === self.selWall ? ' is-sel' : ''), 'data-wall': i });
      li.appendChild(h('div', { class: 'cw-row-top' }, [
        h('span', { class: 'cw-row-k', text: 'Wall ' + wallLetter(i) }),
        h('span', { class: 'cw-row-dim', 'data-wall-dim': i }),
        walls.length > def.min_items && m.mode !== 'teaser' ? h('button', { type: 'button', class: 'cw-del', 'aria-label': 'Remove wall ' + wallLetter(i), onclick: function () {
          walls.splice(i, 1);
          self.selWall = Math.min(self.selWall, walls.length - 1);
          self.renderWalls();
          self.update();
          self.say('Wall removed.');
          var b = self.wallList.querySelector('.cf-input');
          if (b) { b.focus(); }
        } }, [icon(I.close, 13)]) : null
      ]));
      var dims = h('div', { class: 'cw-dims' });
      def.fields.forEach(function (fd) {
        dims.appendChild(self.numberField(fd, ['values', 'walls', i, fd.id], { label: fd.label + (walls.length > 1 || m.mode !== 'teaser' ? ', wall ' + wallLetter(i) : ''), noHelp: i > 0, noSource: i > 0 }));
      });
      li.appendChild(dims);
      li.addEventListener('focusin', function () {
        if (self.selWall !== i) { self.selWall = i; self.markSelWall(); self.update(); }
      });
      self.wallList.appendChild(li);
    });
    if (this.addWallBtn) { this.addWallBtn.disabled = walls.length >= def.max_items; }
    /* visible labels stay short; the aria label carries the wall letter */
    this.wallList.querySelectorAll('.cf-label').forEach(function (l) { l.textContent = l.textContent.replace(/, wall [A-Z]$/, ''); });
    this.wallList.querySelectorAll('.cf-input').forEach(function (inp) {
      var lab = self.wallList.querySelector('label[for="' + inp.id + '"]');
      var row = inp.closest('.cw-row');
      if (lab && row) { inp.setAttribute('aria-label', lab.textContent + ', wall ' + wallLetter(parseInt(row.getAttribute('data-wall'), 10))); }
    });
  };
  Calc.prototype.markSelWall = function () {
    var self = this;
    if (this.wallList) {
      Array.prototype.forEach.call(this.wallList.children, function (li, i) { li.classList.toggle('is-sel', i === self.selWall); });
    }
  };

  /* -------- AR stage: one-point perspective LiDAR room, the selected wall on the back plane */
  Calc.prototype.stage = function () {
    var self = this;
    var st = h('div', { class: 'cs-stage' });
    this.svg = s('svg', { class: 'cs-mesh', viewBox: '0 0 640 480', preserveAspectRatio: 'none', 'aria-hidden': 'true', focusable: 'false' });
    st.appendChild(this.svg);
    this.wallBox = h('div', { class: 'cs-wall', role: 'img' }, [
      h('span', { class: 'cs-wall-clip', 'aria-hidden': 'true' }, [
        h('span', { class: 'cs-seams' }),
        h('span', { class: 'cs-sweep' })
      ]),
      h('span', { class: 'cs-corner cs-corner--tl', 'aria-hidden': 'true' }),
      h('span', { class: 'cs-corner cs-corner--tr', 'aria-hidden': 'true' }),
      h('span', { class: 'cs-corner cs-corner--br', 'aria-hidden': 'true' }),
      h('span', { class: 'cs-corner cs-corner--bl', 'aria-hidden': 'true' }),
      h('span', { class: 'cs-read', 'aria-hidden': 'true' }),
      h('span', { class: 'cs-dim cs-dim--w', 'aria-hidden': 'true' }, [h('span', { class: 'cs-dim-line' }), h('span', { class: 'cs-dim-tag' })]),
      h('span', { class: 'cs-dim cs-dim--h', 'aria-hidden': 'true' }, [h('span', { class: 'cs-dim-line' }), h('span', { class: 'cs-dim-tag' })])
    ]);
    st.appendChild(this.wallBox);
    st.appendChild(h('span', { class: 'cs-chip cs-chip--tl', 'aria-hidden': 'true' }, [h('span', { class: 'cs-blink' }), 'LIDAR MESH · ROOM 01']));
    this.stageLock = h('span', { class: 'cs-chip cs-chip--bl', 'aria-hidden': 'true' });
    this.stageRoll = h('span', { class: 'cs-chip cs-chip--br', 'aria-hidden': 'true' });
    st.appendChild(this.stageLock);
    st.appendChild(this.stageRoll);
    this.wallTabs = h('div', { class: 'cs-tabs', role: 'group', 'aria-label': 'Show wall' });
    st.appendChild(this.wallTabs);
    this.meshKey = '';
    void self;
    return st;
  };

  function lerp(a, b, t) { return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]; }
  function pts(arr) { return arr.map(function (p) { return p[0].toFixed(1) + ',' + p[1].toFixed(1); }).join(' '); }

  Calc.prototype.drawStage = function (r) {
    if (!this.svg) { return; }
    var m = this.m, self = this;
    var walls = m.values.walls;
    var i = Math.min(this.selWall, walls.length - 1);
    var wr = r.walls[i] || { width_ft: 12, height_ft: 9, panels: 0, area: 0 };
    var wf = isNum(wr.width_ft) && wr.width_ft > 0 ? wr.width_ft : 12;
    var hf = isNum(wr.height_ft) && wr.height_ft > 0 ? wr.height_ft : 9;
    /* back plane rectangle (schematic: aspect kept within limits) */
    var ar = wf / hf, MW = 360, MH = 250, W, H;
    if (ar > MW / MH) { W = MW; H = MW / ar; } else { H = MH; W = MH * ar; }
    W = Math.max(64, W); H = Math.max(48, H);
    var x0 = 320 - W / 2, y0 = 238 - H / 2, x1 = x0 + W, y1 = y0 + H;
    var key = [x0, y0, x1, y1].map(function (v) { return Math.round(v); }).join(',');
    if (key !== this.meshKey) {
      this.meshKey = key;
      var svg = this.svg;
      while (svg.firstChild) { svg.removeChild(svg.firstChild); }
      var C = { tl: [0, 0], tr: [640, 0], br: [640, 480], bl: [0, 480] };
      var B = { tl: [x0, y0], tr: [x1, y0], br: [x1, y1], bl: [x0, y1] };
      var planes = [
        { k: 'ceil', p: [C.tl, C.tr, B.tr, B.tl], back: [B.tl, B.tr], front: [C.tl, C.tr] },
        { k: 'floor', p: [B.bl, B.br, C.br, C.bl], back: [B.bl, B.br], front: [C.bl, C.br] },
        { k: 'left', p: [C.tl, B.tl, B.bl, C.bl], back: [B.tl, B.bl], front: [C.tl, C.bl] },
        { k: 'right', p: [B.tr, C.tr, C.br, B.br], back: [B.tr, B.br], front: [C.tr, C.br] }
      ];
      planes.forEach(function (pl) {
        var g = s('g', { class: 'cs-plane cs-plane--' + pl.k });
        g.appendChild(s('polygon', { points: pts(pl.p), class: 'cs-plane-fill' }));
        var DEPTH = [0.1, 0.23, 0.39, 0.58, 0.8];
        DEPTH.forEach(function (t) {
          var a = lerp(pl.back[0], pl.front[0], t), b = lerp(pl.back[1], pl.front[1], t);
          g.appendChild(s('line', { x1: a[0], y1: a[1], x2: b[0], y2: b[1], class: 'cs-gl' }));
        });
        var N = 8;
        for (var k = 1; k < N; k++) {
          var bp = lerp(pl.back[0], pl.back[1], k / N), fp = lerp(pl.front[0], pl.front[1], k / N);
          g.appendChild(s('line', { x1: bp[0], y1: bp[1], x2: fp[0], y2: fp[1], class: 'cs-gl' }));
        }
        g.appendChild(s('polygon', { points: pts(pl.p), class: 'cs-plane-edge' }));
        svg.appendChild(g);
      });
      /* floor scan points */
      [[0.3, 0.32], [0.62, 0.5], [0.42, 0.78], [0.8, 0.24], [0.18, 0.62]].forEach(function (d, k) {
        var a = lerp(B.bl, C.bl, d[1]), b = lerp(B.br, C.br, d[1]);
        var p = lerp(a, b, d[0]);
        svg.appendChild(s('circle', { cx: p[0].toFixed(1), cy: p[1].toFixed(1), r: 3.4, class: 'cs-pt' + (k % 2 ? ' cs-pt--b' : '') }));
      });
      /* wall box in % of the 640x480 stage */
      var bx = this.wallBox.style;
      bx.left = (x0 / 6.4).toFixed(3) + '%';
      bx.top = (y0 / 4.8).toFixed(3) + '%';
      bx.width = (W / 6.4).toFixed(3) + '%';
      bx.height = (H / 4.8).toFixed(3) + '%';
    }
    /* seams: panel strips measured from the left edge at the coverage width */
    var seams = this.wallBox.querySelector('.cs-seams');
    seams.innerHTML = '';
    var covFt = isNum(r.coveragePerPanelIn) && r.coveragePerPanelIn > 0 ? r.coveragePerPanelIn / 12 : null;
    var p = wr.panels || 0;
    if (covFt && p > 0) {
      var stripPct = covFt / wf * 100;
      var showNums = p <= 24 && stripPct * W / 100 >= 18;
      for (var k = 0; k < p; k++) {
        var left = k * stripPct, width = Math.min(stripPct, 100 - left);
        if (width <= 0) { break; }
        var strip = h('span', { class: 'cs-strip' + (k % 2 ? ' is-odd' : '') });
        strip.style.left = left.toFixed(3) + '%';
        strip.style.width = width.toFixed(3) + '%';
        if (showNums) { strip.appendChild(h('span', { class: 'cs-strip-n', text: 'P' + (k + 1) })); }
        seams.appendChild(strip);
      }
      seams.classList.toggle('is-dense', p > 40);
    }
    var L = wallLetter(i);
    this.wallBox.querySelector('.cs-dim--w .cs-dim-tag').textContent = 'W · ' + feetInches(wf);
    this.wallBox.querySelector('.cs-dim--h .cs-dim-tag').textContent = 'H · ' + feetInches(hf);
    this.wallBox.querySelector('.cs-read').textContent = 'WALL ' + L + ' · ' + fmt(wr.area, 'd1') + ' SQ FT · ' + p + ' ' + plural(p, 'PANEL', 'PANELS');
    this.wallBox.setAttribute('aria-label', 'Diagram of wall ' + L + ': ' + fmt(wf) + ' ft wide by ' + fmt(hf) + ' ft tall, ' + fmt(wr.area, 'd1') + ' sq ft, ' + p + ' printed ' + plural(p, 'panel', 'panels') + '.');
    this.stageLock.textContent = 'FLOOR · CEILING · WALL ' + L + ' — LOCKED';
    this.stageRoll.textContent = fmt(num(m.settings.rollWidthIn)) + '″ ROLL · ' + (covFt ? fmt(r.coveragePerPanelIn) + '″ PER PANEL' : 'CHECK PANEL WIDTH');
    /* wall tabs */
    var tabs = this.wallTabs;
    if (walls.length > 1) {
      if (tabs.children.length !== walls.length) {
        tabs.innerHTML = '';
        walls.forEach(function (w, k) {
          var b = h('button', { type: 'button', class: 'cs-tab', 'aria-label': 'Show wall ' + wallLetter(k), text: wallLetter(k) });
          b.addEventListener('click', function () { self.selWall = k; self.markSelWall(); self.update(); });
          tabs.appendChild(b);
        });
      }
      Array.prototype.forEach.call(tabs.children, function (b, k) { b.setAttribute('aria-pressed', String(k === i)); });
      tabs.hidden = false;
    } else {
      tabs.innerHTML = '';
      tabs.hidden = true;
    }
  };

  Calc.prototype.wallResults = function () {
    var m = this.m;
    var out = h('div', { class: 'calc-out', id: this.uid + '-out', role: 'region', 'aria-label': 'Results' });
    this.out.heroV = h('span', { class: 'cr-hero-v' });
    this.out.heroSub = h('span', { class: 'cr-hero-sub' });
    out.appendChild(h('div', { class: 'cr-hero' }, [
      this.out.heroV,
      h('span', { class: 'cr-hero-k' }, [h('span', { class: 'cr-hero-u', text: 'SQ FT · WALL AREA TO COVER' }), this.out.heroSub]),
      h('span', { class: 'cr-hero-chip', 'aria-hidden': 'true', text: 'AUTO-CALC' })
    ]));
    var tiles = h('dl', { class: 'cr-tiles' });
    var self = this;
    function tile(key, label, cls) {
      var v = h('dd', { class: 'cr-tile-v' }), sub = h('dd', { class: 'cr-tile-sub' });
      var k = h('dt', { class: 'cr-tile-k', text: label });
      tiles.appendChild(h('div', { class: 'cr-tile' + (cls ? ' ' + cls : '') }, [k, v, sub]));
      self.out[key] = { v: v, sub: sub, k: k };
    }
    tile('panels', 'Printed panels');
    if (m.mode !== 'teaser') { tile('material', 'Printed material at 54-inch roll width'); }
    tile('range', 'Estimated installed range', 'cr-tile--range');
    out.appendChild(tiles);
    this.out.openMsg = h('p', { class: 'cr-warn', hidden: true });
    out.appendChild(this.out.openMsg);
    if (m.mode !== 'teaser') {
      this.out.msgs = h('ul', { class: 'cr-msgs', role: 'list' });
      out.appendChild(h('div', { class: 'cr-notes' }, [h('p', { class: 'cr-notes-k', text: 'Notes for your wall' }), this.out.msgs]));
    }
    return out;
  };

  Calc.prototype.lensResults = function () {
    var out = h('div', { class: 'calc-out calc-out--lens', id: this.uid + '-out', role: 'region', 'aria-label': 'Results' });
    this.out.lensHero = h('span', { class: 'cr-hero-v' });
    this.out.lensHeroSub = h('span', { class: 'cr-hero-sub' });
    out.appendChild(h('div', { class: 'cr-hero cr-hero--lens' }, [
      h('span', { class: 'cr-hero-k' }, [h('span', { class: 'cr-hero-u', text: 'COST PER DAY ON THE WALL' }), this.out.lensHeroSub]),
      this.out.lensHero
    ]));
    var tiles = h('dl', { class: 'cr-tiles' });
    var self = this;
    [['costPerYear', 'Cost per year on the wall'], ['costPerOpenDay', 'Cost per day you\'re open'], ['costPerView', 'Cost per view'], ['costPerEmployeePerYear', 'Cost per employee per year']].forEach(function (t) {
      var v = h('dd', { class: 'cr-tile-v' }), sub = h('dd', { class: 'cr-tile-sub' });
      var box = h('div', { class: 'cr-tile' }, [h('dt', { class: 'cr-tile-k', text: t[1] }), v, sub]);
      tiles.appendChild(box);
      self.out[t[0]] = { v: v, sub: sub, box: box };
    });
    out.appendChild(tiles);
    this.out.empty = h('p', { class: 'cr-empty' });
    out.appendChild(this.out.empty);
    this.out.lensBar = h('div', { class: 'cr-spread', 'aria-hidden': 'true' });
    out.appendChild(this.out.lensBar);
    return out;
  };

  Calc.prototype.updateWall = function (first) {
    var m = this.m, o = this.out;
    var r = wallCost(m.values, m.settings);
    this.res = r;
    /* per-wall dims in rows */
    if (this.wallList) {
      Array.prototype.forEach.call(this.wallList.querySelectorAll('[data-wall-dim]'), function (el) {
        var w = r.walls[parseInt(el.getAttribute('data-wall-dim'), 10)];
        el.textContent = w && w.ok ? fmt(w.area, 'd1') + ' SQ FT · ' + w.panels + ' ' + plural(w.panels, 'PANEL', 'PANELS') : '';
      });
    }
    this.drawStage(r);
    if (m.mode === 'roi_lens') {
      this.updateLens(r, first);
      return;
    }
    o.heroV.textContent = fmt(r.netSqFt, 'd1');
    var dims = r.walls.length === 1 && r.walls[0].ok
      ? fmt(r.walls[0].width_ft, 'd2') + ' FT × ' + fmt(r.walls[0].height_ft, 'd2') + ' FT'
      : r.walls.length + ' WALLS · ' + fmt(r.grossSqFt, 'd1') + ' SQ FT GROSS';
    if (r.openingsUsed > 0) { dims += ' − ' + fmt(r.openingsUsed, 'd1') + ' OPENINGS'; }
    o.heroSub.textContent = dims;
    o.panels.v.textContent = fmt(r.panelsTotal, 'int');
    o.panels.sub.textContent = r.coverageOk
      ? (r.walls.length > 1 ? 'Per wall: ' + r.walls.map(function (w, i) { return wallLetter(i) + ' ' + w.panels; }).join(' · ') : 'About ' + fmt(r.coveragePerPanelIn) + ' in of wall per panel')
      : 'Usable print width must be larger than the overlap.';
    if (o.material) {
      o.material.k.textContent = 'Printed material at ' + fmt(num(m.settings.rollWidthIn)) + '-inch roll width';
      o.material.v.textContent = fmt(r.billedMaterialSqFt, 'd1') + ' sq ft';
      o.material.sub.textContent = 'Full roll width plus trim. Openings are not deducted.';
    }
    if (r.priceMode) {
      o.range.v.textContent = fmt(r.low, 'money0') + ' to ' + fmt(r.high, 'money0');
      o.range.v.classList.remove('is-prompt');
      o.range.sub.textContent = r.designPending ? 'Plus design, quoted separately.' : 'Estimate only. Your exact price comes from a free on-site measure.';
    } else {
      o.range.v.textContent = RATE_PLACEHOLDER;
      o.range.v.classList.add('is-prompt');
      o.range.sub.textContent = '[estimated range after your free on-site measure]';
    }
    o.openMsg.hidden = !r.openingsCapped;
    o.openMsg.textContent = r.openingsCapped ? 'Openings are larger than the wall area.' : '';
    var of = this.f['values.openings_sqft'];
    if (of && r.openingsCapped) { of.msg.hidden = false; of.msg.textContent = 'Openings are larger than the wall area.'; }
    else if (of && document.activeElement !== of.input) { of.msg.hidden = true; }
    if (o.msgs) {
      o.msgs.innerHTML = '';
      if (!r.messages.length) { o.msgs.appendChild(h('li', { class: 'cr-msg is-none', text: 'Nothing special to flag. We confirm the wall at the free on-site measure.' })); }
      r.messages.forEach(function (msg) {
        var li = h('li', { class: 'cr-msg' });
        if (msg.link_url) {
          var at = msg.text.indexOf(msg.link_text);
          li.appendChild(document.createTextNode(msg.text.slice(0, at)));
          li.appendChild(h('a', { href: msg.link_url, text: msg.link_text }));
          li.appendChild(document.createTextNode(msg.text.slice(at + msg.link_text.length)));
        } else { li.textContent = msg.text; }
        o.msgs.appendChild(li);
      });
    }
    if (this.mini) {
      this.mini.querySelector('.calc-mini-k').textContent = 'Wall area · panels';
      this.mini.querySelector('.calc-mini-v').textContent = fmt(r.netSqFt, 'd1') + ' sq ft · ' + r.panelsTotal + ' ' + plural(r.panelsTotal, 'panel', 'panels');
    }
    if (!first) {
      this.say('Wall area to cover ' + fmt(r.netSqFt, 'd1') + ' square feet. ' + r.panelsTotal + ' printed ' + plural(r.panelsTotal, 'panel', 'panels') + '.' + (r.priceMode ? ' Estimated range ' + fmt(r.low, 'money0') + ' to ' + fmt(r.high, 'money0') + '.' : ''));
    }
  };

  Calc.prototype.updateLens = function (wr, first) {
    var m = this.m, o = this.out;
    /* price mode: the estimate midpoint prefills project cost until the visitor types one */
    if (m.priceMode && wr && isNum(wr.installedMid) && !this.lensTouched) {
      var mid = roundTo(wr.installedMid, num(m.settings.roundTo) || 1);
      if (m.values.project_cost !== mid) {
        m.values.project_cost = mid;
        var pf = this.f['values.project_cost'];
        if (pf) { this.syncField(pf); }
      }
    }
    var r = roiLens(m.values);
    var y = num(m.values.years_on_wall);
    o.lensHero.textContent = r.ready ? fmt(r.costPerCalendarDay, 'money2') : '[project cost] / ([years] × 365)';
    o.lensHero.classList.toggle('is-prompt', !r.ready);
    o.lensHeroSub.textContent = r.ready ? fmt(m.values.project_cost, 'money0') + ' over ' + fmt(y) + ' ' + plural(y, 'year', 'years') : 'Formula demo until you enter a project cost';
    function show(key, val, f, sub, need) {
      var t = o[key];
      if (isNum(val)) { t.v.textContent = fmt(val, f); t.v.classList.remove('is-prompt'); t.sub.textContent = sub || ''; }
      else { t.v.textContent = need; t.v.classList.add('is-prompt'); t.sub.textContent = ''; }
      t.box.classList.toggle('is-off', !isNum(val));
    }
    show('costPerYear', r.costPerYear, 'money0', '', 'Enter a project cost');
    show('costPerOpenDay', r.costPerOpenDay, 'money2', isNum(r.costPerOpenDay) ? fmt(m.values.days_open_per_year) + ' open days a year' : '', 'Enter a project cost');
    show('costPerView', r.costPerView, 'money3', isNum(r.costPerView) ? fmt(m.values.people_per_day) + ' people a day' : '', r.ready ? 'Add people per day' : 'Enter a project cost');
    show('costPerEmployeePerYear', r.costPerEmployeePerYear, 'money2', '', r.ready ? 'Optional: add employees' : 'Enter a project cost');
    o.empty.innerHTML = '';
    if (!r.ready) {
      o.empty.appendChild(document.createTextNode('Enter a project cost to see the numbers. Need a real one? '));
      var q = this.section.querySelector('[data-calc-cta] a');
      o.empty.appendChild(h('a', { href: q ? q.getAttribute('href') : '/pages/get-a-quote', text: 'Get a free on-site estimate' }));
      o.empty.appendChild(document.createTextNode('.'));
    }
    o.empty.hidden = r.ready;
    /* years ruler */
    o.lensBar.innerHTML = '';
    var yrs = isNum(y) ? y : 3;
    var whole = Math.min(15, Math.ceil(yrs));
    for (var k = 0; k < whole; k++) {
      var seg = h('span', { class: 'cr-spread-y' }, [h('span', { class: 'cr-spread-n', text: 'Y' + (k + 1) })]);
      var frac = Math.min(1, yrs - k);
      seg.style.setProperty('--f', frac.toFixed(3));
      o.lensBar.appendChild(seg);
    }
    if (this.mini) {
      this.mini.querySelector('.calc-mini-k').textContent = 'Cost per day on the wall';
      this.mini.querySelector('.calc-mini-v').textContent = r.ready ? fmt(r.costPerCalendarDay, 'money2') : 'Enter a project cost';
    }
    if (!first) { this.say(r.ready ? 'Cost per day on the wall ' + fmt(r.costPerCalendarDay, 'money2') + '.' : 'Enter a project cost to see the numbers.'); }
  };

  /* ================================================================ wall_vs UI */
  Calc.prototype.renderVs = function () {
    var self = this, m = this.m, app = this.app;
    app.appendChild(h('p', { class: 'calc-banner' }, [h('span', { class: 'calc-banner-dot', 'aria-hidden': 'true' }), 'Defaults are placeholders. Change any number.']));
    var shared = h('div', { class: 'vs-shared' }, [
      this.field(m.inputs.area_sqft, ['values', 'area_sqft']),
      this.field(m.inputs.horizon_years, ['values', 'horizon_years']),
      this.field(m.inputs.downtime_cost_per_day, ['values', 'downtime_cost_per_day']),
      this.field(m.inputs.include_final_removal, ['values', 'include_final_removal'])
    ]);
    app.appendChild(this.step(1, 'Your space', [shared], 'calc-step--wide'));
    var cols = h('div', { class: 'vs-cols' });
    this.vsCards = {};
    m.options.forEach(function (o) {
      var card = h('fieldset', { class: 'vs-card' + (o.accent ? ' is-accent' : ''), 'data-opt': o.id });
      card.appendChild(h('legend', { class: 'vs-card-k' }, [h('span', { class: 'vs-card-dot', 'aria-hidden': 'true' }), o.label]));
      o.assumptions.forEach(function (a) { card.appendChild(self.field(a, ['opt', o.id, a.id], { compact: true })); });
      if (o.note) { card.appendChild(h('p', { class: 'vs-card-note', text: o.note })); }
      if (o.id === 'painted_mural') {
        card.appendChild(h('button', { type: 'button', class: 'vs-hide', onclick: function () { o.visible = false; self.syncVsVis(); self.update(); self.muralBtn.focus(); } }, [icon(I.close, 12), 'Remove this column']));
      }
      self.vsCards[o.id] = card;
      cols.appendChild(card);
    });
    this.muralBtn = h('button', { type: 'button', class: 'cw-add vs-add' }, [icon(I.plus, 14), 'Add a hand-painted mural']);
    this.muralBtn.addEventListener('click', function () {
      m.options[3].visible = true;
      self.syncVsVis();
      self.update();
      var f = self.vsCards.painted_mural.querySelector('.cf-input');
      if (f) { f.focus(); }
    });
    app.appendChild(this.step(2, 'Prices and lifespans', [cols, this.muralBtn], 'calc-step--wide'));

    var out = h('div', { class: 'calc-out vs-out', id: this.uid + '-out', role: 'region', 'aria-label': 'Results' });
    this.out.tableWrap = h('div', { class: 'calc-table-wrap', tabindex: '0', role: 'region', 'aria-label': 'Side-by-side comparison' });
    this.out.track = h('div', { class: 'vs-track' });
    this.out.lowest = h('div', { class: 'cr-tile' });
    this.out.be = h('div', { class: 'cr-tile' });
    this.out.notInc = h('p', { class: 'vs-noinc' });
    out.appendChild(h('div', { class: 'vs-out-top' }, [h('p', { class: 'calc-out-k' }, [h('span', { class: 'calc-live-dot', 'aria-hidden': 'true' }), 'Side-by-side over your time horizon'])]));
    out.appendChild(this.out.tableWrap);
    out.appendChild(h('div', { class: 'vs-sum' }, [this.out.lowest, this.out.be]));
    out.appendChild(this.out.track);
    out.appendChild(this.out.notInc);
    out.appendChild(h('p', { class: 'vs-note', text: WHOLE_INSTALLS_NOTE }));
    out.appendChild(this.ctaRow());
    app.appendChild(this.step(3, 'Results', [out], 'calc-step--wide calc-step--out'));
    this.syncVsVis();
  };
  Calc.prototype.syncVsVis = function () {
    var self = this;
    this.m.options.forEach(function (o) { self.vsCards[o.id].hidden = !o.visible; });
    this.muralBtn.hidden = this.m.options[3].visible;
  };
  Calc.prototype.updateVs = function (first) {
    var m = this.m, o = this.out, self = this;
    var vis = m.options.filter(function (x) { return x.visible; }).map(function (x) { return x.id; });
    var r = wallVs(m.values, m.opt, vis);
    this.res = r;
    var byId = {};
    m.options.forEach(function (x) { byId[x.id] = x; });
    /* table */
    var t = h('table', { class: 'calc-table vs-table' });
    t.appendChild(h('caption', { class: 'cwv-sr-only', text: 'Paint, wallpaper and wall wrap: installs, total cost, cost per year and per square foot per year' }));
    t.appendChild(h('thead', null, [h('tr', null, ['Option', 'Installs in horizon', 'Total cost', 'Cost per year', 'Cost per sq ft per year'].map(function (c, i) { return h('th', { scope: 'col', class: i ? 'num' : '', text: c }); }))]));
    var tb = h('tbody');
    vis.forEach(function (id) {
      var x = r.options[id];
      var tr = h('tr', { class: (id === r.lowest ? 'is-hl' : '') + (byId[id].accent ? ' is-accent' : '') });
      tr.appendChild(h('th', { scope: 'row', text: byId[id].label }));
      if (x.computed) {
        [fmt(x.cycles, 'int'), fmt(x.total, 'money0'), fmt(x.perYear, 'money0'), fmt(x.perSqFtPerYear, 'money2')].forEach(function (c) { tr.appendChild(h('td', { class: 'num', text: c })); });
      } else {
        tr.appendChild(h('td', { class: 'num is-prompt', colspan: '4', text: '[enter your numbers]' }));
      }
      tb.appendChild(tr);
    });
    t.appendChild(tb);
    o.tableWrap.innerHTML = '';
    o.tableWrap.appendChild(t);
    /* summary tiles */
    o.lowest.innerHTML = '';
    o.lowest.appendChild(h('p', { class: 'cr-tile-k', text: 'Lowest cost per year' }));
    if (r.lowest) {
      o.lowest.appendChild(h('p', { class: 'cr-tile-v', text: byId[r.lowest].label }));
      o.lowest.appendChild(h('p', { class: 'cr-tile-sub', text: fmt(r.options[r.lowest].perYear, 'money0') + ' per year · computed options only' }));
    } else {
      o.lowest.appendChild(h('p', { class: 'cr-tile-v is-prompt', text: 'Enter an installed cost and a life for at least one option' }));
    }
    o.be.innerHTML = '';
    o.be.appendChild(h('p', { class: 'cr-tile-k', text: 'Wrap life needed to match the lowest-cost option' }));
    if (r.breakEvenStatus === 'ok') {
      o.be.appendChild(h('p', { class: 'cr-tile-v', text: r.breakEvenLife + ' ' + plural(r.breakEvenLife, 'year', 'years') }));
      o.be.appendChild(h('p', { class: 'cr-tile-sub', text: 'Whole years, compared with ' + fmt(r.breakEvenTarget, 'money0') + ' per year' }));
    } else if (r.breakEvenStatus === 'none') {
      o.be.appendChild(h('p', { class: 'cr-tile-v', text: 'Not within this horizon.' }));
    } else {
      o.be.appendChild(h('p', { class: 'cr-tile-v is-prompt', text: 'Needs a wall wrap installed cost plus at least one other option with numbers' }));
    }
    /* install track */
    o.track.innerHTML = '';
    var hz = num(m.values.horizon_years) || 0;
    o.track.appendChild(h('p', { class: 'calc-table-cap', text: 'Installs across ' + fmt(hz) + ' ' + plural(hz, 'year', 'years') }));
    var maxPY = 0;
    vis.forEach(function (id) { if (r.options[id].computed) { maxPY = Math.max(maxPY, r.options[id].perYear); } });
    vis.forEach(function (id) {
      var x = r.options[id], life = num(m.opt[id].life_years);
      var lane = h('div', { class: 'vs-lane' + (byId[id].accent ? ' is-accent' : '') + (x.computed ? '' : ' is-empty') });
      lane.appendChild(h('span', { class: 'vs-lane-k', text: byId[id].short }));
      var bar = h('span', { class: 'vs-lane-bar', 'aria-hidden': 'true' });
      if (hz > 0) {
        for (var yk = 1; yk < hz; yk++) { var tk = h('span', { class: 'vs-tick' }); tk.style.left = (yk / hz * 100).toFixed(3) + '%'; bar.appendChild(tk); }
        if (isNum(life) && life > 0) {
          var cyc = ceilG(hz / life);
          for (var c = 0; c < cyc && c < 60; c++) {
            var mk = h('span', { class: 'vs-mark' });
            mk.style.left = Math.min(100, c * life / hz * 100).toFixed(3) + '%';
            bar.appendChild(mk);
          }
        }
      }
      lane.appendChild(bar);
      var perYear = x.computed ? fmt(x.perYear, 'money0') + '/yr' : '–';
      var meter = h('span', { class: 'vs-lane-v' }, [
        h('span', { class: 'vs-meter', 'aria-hidden': 'true' }, [h('span', { class: 'vs-meter-f' })]),
        h('span', { class: 'vs-lane-n', text: (x.computed ? x.cycles + ' ' + plural(x.cycles, 'install', 'installs') + ' · ' : '') + perYear })
      ]);
      if (x.computed && maxPY > 0) { meter.firstChild.firstChild.style.width = Math.max(3, x.perYear / maxPY * 100).toFixed(2) + '%'; }
      lane.appendChild(meter);
      o.track.appendChild(lane);
    });
    /* not included */
    var names = { removal_cost_per_sqft: 'removal', maintenance_per_sqft_per_year: 'upkeep', downtime_days: 'days disrupted' };
    var bits = [];
    vis.forEach(function (id) {
      var x = r.options[id];
      if (x.computed && x.notIncluded.length) { bits.push(byId[id].short + ': ' + x.notIncluded.map(function (k) { return names[k]; }).join(', ')); }
    });
    o.notInc.textContent = bits.length ? 'Not included (left blank, counted as 0): ' + bits.join(' · ') + '.' : '';
    o.notInc.hidden = !bits.length;
    if (!first) {
      this.say(r.lowest ? 'Lowest cost per year: ' + byId[r.lowest].label + ', ' + fmt(r.options[r.lowest].perYear, 'money0') + '.' : 'Enter an installed cost and a life for at least one option.');
    }
    void self;
  };

  /* ================================================================ reface UI */
  Calc.prototype.renderReface = function () {
    var m = this.m, app = this.app;
    var ui = h('div', { class: 'calc-ui calc-ui--rf' });
    var inputs = h('div', { class: 'calc-inputs' });
    var items = [this.field(m.inputs.item_type, ['values', 'item_type']), this.field(m.inputs.quantity, ['values', 'quantity']), this.field(m.inputs.area_per_item_sqft, ['values', 'area_per_item_sqft'])];
    if (m.inputs.shape.options.length > 1) { items.push(this.field(m.inputs.shape, ['values', 'shape'])); }
    inputs.appendChild(this.step(1, 'What and how many', items));
    inputs.appendChild(this.step(2, 'Replacing', [
      this.field(m.inputs.replace_cost_per_item, ['values', 'replace_cost_per_item']),
      this.field(m.inputs.replace_demo_per_item, ['values', 'replace_demo_per_item']),
      this.field(m.inputs.replace_install_per_item, ['values', 'replace_install_per_item']),
      this.field(m.inputs.downtime_days_replace, ['values', 'downtime_days_replace'])
    ]));
    inputs.appendChild(this.step(3, 'Refacing and downtime', [
      this.field(m.inputs.downtime_days_reface, ['values', 'downtime_days_reface']),
      this.field(m.inputs.downtime_cost_per_day, ['values', 'downtime_cost_per_day'])
    ]));
    inputs.appendChild(this.ctaRow());

    var out = h('div', { class: 'calc-out', id: this.uid + '-out', role: 'region', 'aria-label': 'Results' });
    this.out.beV = h('span', { class: 'cr-hero-v' });
    this.out.beSub = h('span', { class: 'cr-hero-sub' });
    out.appendChild(h('div', { class: 'cr-hero cr-hero--lens' }, [
      h('span', { class: 'cr-hero-k' }, [h('span', { class: 'cr-hero-u', text: 'MOST REFACING COULD COST PER SQ FT AND STILL BEAT REPLACING' }), this.out.beSub]),
      this.out.beV
    ]));
    var tiles = h('dl', { class: 'cr-tiles' });
    var self = this;
    [['replaceTotal', 'Replace total'], ['refaceTotal', 'Reface total'], ['savings', 'Savings'], ['annualized', 'Cost per year of life']].forEach(function (t) {
      var v = h('dd', { class: 'cr-tile-v' }), sub = h('dd', { class: 'cr-tile-sub' });
      tiles.appendChild(h('div', { class: 'cr-tile' }, [h('dt', { class: 'cr-tile-k', text: t[1] }), v, sub]));
      self.out[t[0]] = { v: v, sub: sub };
    });
    out.appendChild(tiles);
    this.out.bars = h('div', { class: 'rf-bars', 'aria-hidden': 'true' });
    out.appendChild(this.out.bars);
    out.appendChild(h('p', { class: 'cr-warn cr-warn--info', text: LIMITS_NOTE }));
    ui.appendChild(out);
    ui.appendChild(inputs);
    app.appendChild(ui);
    app.appendChild(this.assumptions(RF_SETTINGS, 'Refacing assumptions'));
    this.assumeBody.appendChild(h('p', { class: 'calc-assume-rate' }, [
      h('span', { class: 'cf-tag cf-tag--' + (isNum(m.settings.refaceCostPerSqFt) ? 'sourced' : 'missing'), text: isNum(m.settings.refaceCostPerSqFt) ? 'Set by Ardor' : 'Not set' }),
      ' Installed refacing price per sq ft (film plus labor): ',
      h('b', { text: isNum(m.settings.refaceCostPerSqFt) ? fmt(m.settings.refaceCostPerSqFt, 'money2') + ' per sq ft' : '[reface total after an on-site look]' })
    ]));
  };
  Calc.prototype.updateReface = function (first) {
    var m = this.m, o = this.out;
    var r = reface(m.values, m.settings);
    this.res = r;
    if (isNum(r.breakEvenPrice) && r.breakEvenPrice > 0) {
      o.beV.textContent = fmt(r.breakEvenPrice, 'money2');
      o.beV.classList.remove('is-prompt');
      o.beSub.textContent = 'Per sq ft of surface, film plus labor, for ' + fmt(m.values.quantity) + ' ' + plural(m.values.quantity, 'item', 'items');
    } else if (isNum(r.breakEvenPrice)) {
      o.beV.textContent = 'Not possible at these numbers';
      o.beV.classList.add('is-prompt');
      o.beSub.textContent = 'Prep and downtime already cost more than replacing.';
    } else {
      o.beV.textContent = '[price of a new unit]';
      o.beV.classList.add('is-prompt');
      o.beSub.textContent = 'Enter the price of a new unit to see the break-even price.';
    }
    function set(t, val, f, sub, prompt) {
      if (isNum(val)) { t.v.textContent = fmt(val, f); t.v.classList.remove('is-prompt'); t.sub.textContent = sub || ''; }
      else { t.v.textContent = prompt; t.v.classList.add('is-prompt'); t.sub.textContent = ''; }
    }
    set(o.replaceTotal, r.replaceTotal, 'money0', 'New units, demo, labor and downtime', 'Enter the price of a new unit');
    set(o.refaceTotal, r.refaceTotal, 'money0', 'Area × refacing rate, prep and downtime', '[reface total after an on-site look]');
    set(o.savings, r.savings, 'money0', isNum(r.savingsPct) ? fmt(r.savingsPct, 'pct0') + ' of the replace total' : '', 'Shown when both totals exist');
    if (isNum(r.annualizedReface) || isNum(r.annualizedReplace)) {
      o.annualized.v.textContent = 'Reface ' + fmt(r.annualizedReface, 'money0') + ' · Replace ' + fmt(r.annualizedReplace, 'money0');
      o.annualized.v.classList.remove('is-prompt');
      o.annualized.sub.textContent = 'Total divided by the life you entered';
    } else {
      o.annualized.v.textContent = 'Add both lives under Refacing assumptions';
      o.annualized.v.classList.add('is-prompt');
      o.annualized.sub.textContent = '';
    }
    o.bars.innerHTML = '';
    var mx = Math.max(r.replaceTotal || 0, r.refaceTotal || 0);
    [['Replace', r.replaceTotal, ''], ['Reface', r.refaceTotal, ' is-accent']].forEach(function (b) {
      var fill = h('span', { class: 'rf-bar-f' + b[2] + (isNum(b[1]) ? '' : ' is-empty') });
      fill.style.width = isNum(b[1]) && mx > 0 ? Math.max(3, b[1] / mx * 100).toFixed(2) + '%' : '100%';
      o.bars.appendChild(h('div', { class: 'rf-bar' }, [h('span', { class: 'rf-bar-k', text: b[0] }), h('span', { class: 'rf-bar-t' }, [fill]), h('span', { class: 'rf-bar-v', text: isNum(b[1]) ? fmt(b[1], 'money0') : '–' })]));
    });
    if (!first) { this.say(isNum(r.breakEvenPrice) && r.breakEvenPrice > 0 ? 'Refacing beats replacing below ' + fmt(r.breakEvenPrice, 'money2') + ' per square foot.' : 'Enter the price of a new unit to see the break-even price.'); }
  };

  /* ================================================================ update */
  Calc.prototype.update = function (first) {
    if (this.m.id === 'wall_cost') { this.updateWall(first); }
    else if (this.m.id === 'wall_vs_paint_wallpaper') { this.updateVs(first); }
    else { this.updateReface(first); }
  };

  /* lens: typing a project cost stops the midpoint prefill */
  var _numberField = Calc.prototype.numberField;
  Calc.prototype.numberField = function (def, path, opts) {
    var el = _numberField.call(this, def, path, opts);
    if (path[0] === 'values' && path[1] === 'project_cost') {
      var self = this;
      el.querySelector('.cf-input').addEventListener('input', function () { self.lensTouched = true; });
    }
    return el;
  };

  /* ================================================================ init */
  function initAll(scope) {
    if (!scope || !scope.querySelectorAll) { return; }
    var mounts = scope.querySelectorAll('[data-cwv-calc]');
    for (var i = 0; i < mounts.length; i++) {
      var el = mounts[i];
      if (el.__cwvCalc) { continue; }
      try { el.__cwvCalc = new Calc(el); } catch (e) { if (root.console) { root.console.error('[CWV calculator]', e); } }
    }
  }

  CWV.calc = {
    __loaded: true,
    version: 'sww-1.0',
    defs: { WALL_INPUTS: WALL_INPUTS, WALL_SETTINGS: WALL_SETTINGS, LENS_INPUTS: LENS_INPUTS, VS_INPUTS: VS_INPUTS, VS_OPTIONS: VS_OPTIONS, RF_INPUTS: RF_INPUTS, RF_SETTINGS: RF_SETTINGS },
    wallCost: wallCost,
    roiLens: roiLens,
    wallVs: wallVs,
    vsOption: vsOption,
    reface: reface,
    buildModel: buildModel,
    parseConfig: parseConfig,
    format: fmt,
    feetInches: feetInches,
    initAll: initAll
  };

  if (typeof module !== 'undefined' && module.exports) { module.exports = CWV.calc; }

  if (typeof document !== 'undefined') {
    var start = function () { initAll(document); };
    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', start); } else { start(); }
    document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  }
})(typeof window !== 'undefined' ? window : globalThis);
